import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import "./App.css";
import BrandName from './components/BrandName';
import { 
  trackPageView, 
  trackQuoteSubmission, 
  trackOrderCreation, 
  trackAddToCart, 
  trackDesignView,
  trackContactFormSubmission,
  trackServiceRequest,
  trackUserAuth,
  trackDesignerApplication,
  trackDesignUpload
} from './utils/analytics';
import { Upload, FileText, Zap, Settings, ChevronRight, ChevronDown, Check, CheckCircle, Mail, Phone, MapPin, ArrowLeft, User, Users, LogIn, ShoppingCart, Package, Eye, Edit, X } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CurrencyProvider, useCurrency } from "./contexts/CurrencyContext";
import Profile from "./components/Profile";
import LoginModal from "./components/LoginModal";
import MainNavigation from "./components/MainNavigation";
import ProductsPage from "./components/ProductsPage";
import Footer from "./components/Footer";
import ProductDetailPage from "./components/ProductDetailPage";
import AdminPanel from "./components/AdminPanel";
import CartPage from "./components/CartPage";
import ThreeDPreview from "./components/ThreeDPreview";
import SharedPageLayout from "./components/SharedPageLayout";
import ComingSoon from "./components/ComingSoon";
import CurrencySelector from "./components/CurrencySelector";
import { BACKEND_URL } from "./config";
import { termsOfService, shippingReturns, licensingPolicy, privacyPolicy } from "./data/policies";

// Use runtime-detected backend URL (handles different deployment environments)
const API_BASE = BACKEND_URL;
const API = `${API_BASE}/api`;

// Google Analytics Page View Tracker
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  return null; // This component doesn't render anything
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if coming soon mode is enabled
    const comingSoonEnabled = process.env.REACT_APP_COMING_SOON === 'true';
    if (!comingSoonEnabled) return true; // If not in coming soon mode, allow access
    
    // Check if already authenticated (persists across sessions)
    return localStorage.getItem('site_authenticated') === 'true';
  });

  const handleAuthenticate = () => {
    setIsAuthenticated(true);
  };

  // Check if coming soon mode is enabled
  const comingSoonEnabled = process.env.REACT_APP_COMING_SOON === 'true';

  // If coming soon is enabled and user is not authenticated, show password page
  if (comingSoonEnabled && !isAuthenticated) {
    return <ComingSoon onAuthenticated={handleAuthenticate} />;
  }

  // Otherwise, show the full app
  return (
    <Router>
      <PageViewTracker />
      <AuthProvider>
        <CurrencyProvider>
          <CartProvider>
            <CurrencySelector />
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsRoute />} />
            <Route path="/product/:slug" element={<ProductDetailRoute />} />
            <Route path="/cart" element={<CartRoute />} />
            <Route path="/profile" element={<ProfileRoute />} />
            <Route path="/admin" element={<AdminRoute />} />
            <Route path="/about" element={<AboutRoute />} />
            <Route path="/services" element={<ServicesRoute />} />
            <Route path="/contact" element={<ContactRoute />} />
            <Route path="/marketplace" element={<MarketplaceRoute />} />
            <Route path="/marketplace/listing/:id" element={<ListingDetailRoute />} />
            <Route path="/creator/:username" element={<CreatorProfileRoute />} />
            <Route path="/designer/apply" element={<DesignerApplyRoute />} />
            <Route path="/designer/dashboard" element={<DesignerDashboardRoute />} />
            <Route path="/designer/upload" element={<DesignerUploadRoute />} />
            <Route path="/designer/edit/:id" element={<DesignerEditRoute />} />
            <Route path="/designer/terms" element={<DesignerTermsRoute />} />
            <Route path="/customer/license-terms" element={<CustomerLicenseTermsRoute />} />
            <Route path="/admin/marketplace" element={<AdminMarketplaceRoute />} />
            <Route path="/faq" element={<FAQRoute />} />
            <Route path="/terms" element={<TermsRoute />} />
            <Route path="/file-formats" element={<FileFormatsRoute />} />
            <Route path="/material-guide" element={<MaterialGuideRoute />} />
            <Route path="/quality-standards" element={<QualityStandardsRoute />} />
            <Route path="/shipping-info" element={<ShippingInfoRoute />} />
            
            {/* Policy Pages */}
            <Route path="/terms-of-service" element={<TermsOfServiceRoute />} />
            <Route path="/shipping-returns" element={<ShippingReturnsRoute />} />
            <Route path="/licensing-policy" element={<LicensingPolicyRoute />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </CartProvider>
        </CurrencyProvider>
      </AuthProvider>
    </Router>
  );
};

// Utility function to create SEO-friendly slugs
const createProductSlug = (manufacturer, productName) => {
  const combined = `${manufacturer} ${productName}`;
  return combined
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim('-'); // Remove leading/trailing hyphens
};

// Utility function to parse slug back to search terms
const parseProductSlug = (slug) => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Cart Context Provider with Persistence
const CartContext = React.createContext();

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  // Cart persistence functions
  const CART_STORAGE_KEY = 'create3d_cart';
  const CART_EXPIRY_DAYS = 7;

  const saveCartToStorage = (cartItems) => {
    try {
      const cartData = {
        items: cartItems,
        timestamp: new Date().getTime()
      };
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  };

  const loadCartFromStorage = () => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (!savedCart) return [];

      const cartData = JSON.parse(savedCart);
      const currentTime = new Date().getTime();
      const expiryTime = CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000; // 7 days in milliseconds

      // Check if cart has expired
      if (currentTime - cartData.timestamp > expiryTime) {
        localStorage.removeItem(CART_STORAGE_KEY);
        return [];
      }

      return cartData.items || [];
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return [];
    }
  };

  // Load cart from storage on component mount
  useEffect(() => {
    const savedCartItems = loadCartFromStorage();
    if (savedCartItems.length > 0) {
      setCart(savedCartItems);
      console.log(`Restored ${savedCartItems.length} items from saved cart`);
    }
  }, []);

  // Save cart to storage whenever cart changes
  useEffect(() => {
    if (cart.length > 0) {
      saveCartToStorage(cart);
    } else {
      // Clear storage when cart is empty
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, [cart]);

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
      
      // Track add to cart event in Google Analytics (only for new items)
      trackAddToCart({
        id: product.id,
        name: product.title || product.name,
        price: product.price,
        type: product.type || 'Product'
      });
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item => 
        item.id === productId 
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  return (
    <CartContext.Provider value={{
      cart,
      setCart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = React.useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
// Route Components
const HomePage = () => {
  return <AppContent />;
};



// Coming Soon Password Protection
const ComingSoonPage = ({ onAuthenticate }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Get password from environment or use default
    const correctPassword = process.env.REACT_APP_SITE_PASSWORD || 'launch2025';
    
    if (password === correctPassword) {
      sessionStorage.setItem('siteAuthenticated', 'true');
      onAuthenticate();
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-white mb-4">
            <BrandName size="3xl" className="uppercase" />
          </h1>
          <p className="text-xl text-blue-200 mb-2">Coming Soon</p>
          <p className="text-slate-300">
            We're putting the final touches on something amazing. Enter the password to preview.
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter password"
                required
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-[#0007b5] hover:bg-[#000690] text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Enter Site
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/20 text-center">
            <p className="text-sm text-slate-300">
              Don't have the password? <a href="mailto:hello@create3dworks.com" className="text-blue-400 hover:text-blue-300">Contact us</a>
            </p>
          </div>
        </div>

        <div className="mt-8 text-center text-slate-400 text-sm">
          <p>© 2025 <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span>. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

const ProductsRoute = () => {
  const navigate = useNavigate();
  const { cart, addToCart } = useCart();

  const navigateHome = () => navigate('/');
  const navigateToProduct = (product) => {
    const slug = createProductSlug(product.manufacturer, product.name);
    navigate(`/product/${slug}`);
  };
  const navigateToCart = () => navigate('/cart');
  const navigateToProfile = () => navigate('/profile');

  return (
    <ProductsPage 
      navigateHome={navigateHome} 
      navigateToProduct={navigateToProduct} 
      navigateToCart={navigateToCart} 
      navigateToProfile={navigateToProfile}
      cart={cart} 
      addToCart={addToCart} 
    />
  );
};

const ProductDetailRoute = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { cart, addToCart } = useCart();
  const [productId, setProductId] = useState(null);

  // Extract product info from slug and find the actual product
  useEffect(() => {
    const findProductBySlug = async () => {
      try {
        // Parse the slug to get search terms
        const searchTerms = parseProductSlug(slug);
        
        // Fetch products and find matching one
        const response = await fetch(`${BACKEND_URL}/api/products`);
        const data = await response.json();
        
        // Find product that matches the slug
        const matchingProduct = data.products.find(product => {
          const productSlug = createProductSlug(product.manufacturer, product.name);
          return productSlug === slug;
        });
        
        if (matchingProduct) {
          setProductId(matchingProduct.id);
        } else {
          // If no exact match, redirect to products page
          navigate('/products');
        }
      } catch (error) {
        console.error('Error finding product:', error);
        navigate('/products');
      }
    };

    if (slug) {
      findProductBySlug();
    }
  }, [slug, navigate]);

  const navigateToProducts = () => navigate('/products');
  const navigateHome = () => navigate('/');
  const navigateToCart = () => navigate('/cart');

  // Don't render until we have the productId
  if (!productId) {
    return <div>Loading...</div>;
  }

  return (
    <ProductDetailPage 
      productId={productId} 
      navigateToProducts={navigateToProducts} 
      navigateHome={navigateHome} 
      navigateToCart={navigateToCart} 
      addToCart={addToCart} 
      cart={cart} 
    />
  );
};

const CartRoute = () => {
  const navigate = useNavigate();
  const { cart, setCart, clearCart } = useCart();

  const navigateHome = () => navigate('/');
  const navigateToProducts = () => navigate('/products');

  return (
    <CartPage 
      navigateHome={navigateHome} 
      navigateToProducts={navigateToProducts} 
      cart={cart} 
      setCart={setCart}
      clearCart={clearCart}
    />
  );
};

const ProfileRoute = () => {
  const navigate = useNavigate();
  
  const navigateHome = () => navigate('/');
  const navigateToPage = (page) => navigate(`/${page}`);

  return <Profile navigateHome={navigateHome} navigateToPage={navigateToPage} />;
};

const AdminRoute = () => {
  const navigate = useNavigate();
  const navigateHome = () => navigate('/');

  return <AdminPanel navigateHome={navigateHome} />;
};

// Placeholder route components for other pages
const AboutRoute = () => {
  const navigate = useNavigate();
  
  return (
    <SharedPageLayout currentPage="about">
      {/* Hero Banner - Modern Minimalistic Design */}
      <div className="relative bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-14 md:py-18">
            {/* Title with subtle accent */}
            <div className="max-w-4xl">
              <div className="inline-block mb-4">
                <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">Our Story</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-6 page-title-uncage leading-tight">
                About Us
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Built by engineers, powered by experience, driven by innovation
              </p>
            </div>
            
            {/* Decorative element - subtle accent line */}
            <div className="mt-8 w-24 h-1 bg-gradient-to-r from-[#0007b5] to-blue-400"></div>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#0007b5] rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Mission Statement with Visual */}
        <section className="mb-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-8">
              <div className="rounded-2xl overflow-hidden">
                <img 
                  src="/about_us_illustration_3.png" 
                  alt="3D Printing Technology in Action"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-lg text-slate-700 leading-relaxed mb-6">
                  <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> is more than just a 3D printing company - it is born from hands-on expertise and passion for turning ideas into reality. Backed by the experience of a team of engineers and techs who honed their skills as a "skunkworks" 3D printing team, our roots run deep in solving real-world challenges through advanced additive manufacturing.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  With a combined 8 years of specialized 3D printing experience and over 20 years in engineering and design experience, our team has mastered the full spectrum of part creation specifically for 3D Printing. We've designed, prototyped, tested, and produced 3D printed components for diverse applications, from cosmetic and display pieces to functional and final-use parts leading to 3,000+ lb. of material (filament) printed.
                </p>
              </div>
            </div>
            
            <div className="mt-8">
              <p className="text-lg text-slate-600 leading-relaxed">
                <strong className="text-slate-900">Our Mission:</strong> Combine engineering expertise with manufacturing flexibility to deliver results you can trust. Whether we produce your already designed parts, design your components from scratch, or anywhere in between; we don't just print - we can analyze, optimize, and engineer solutions tailored to your specific requirements.
              </p>
            </div>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="mb-20">
          <div className="relative rounded-3xl p-12 text-white overflow-hidden">
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: 'url(/by_the_numbers_background.jpg)' }}
            ></div>
            {/* Brand Blue Overlay (80% opaque) */}
            <div className="absolute inset-0 bg-[#0007b5] opacity-80"></div>
            
            {/* Content */}
            <div className="relative z-10">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold mb-4">By the Numbers</h2>
                <p className="text-blue-100 text-lg">Real experience you can count on</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-black mb-2">8+ Years</div>
                  <div className="text-blue-200">Combined Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-black mb-2">3,000+ lb</div>
                  <div className="text-blue-200">Material Printed</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-black mb-2">6+</div>
                  <div className="text-blue-200">Use Cases Covered</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-black mb-2">100%</div>
                  <div className="text-blue-200">Quality Focused</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values with Visual Layout */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">What Drives Us</h2>
            <p className="text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed">
              Our experience stemmed from in-house projects where we managed every stage—from initial concept and design development to rigorous testing, iterative revisions, and seamless production. Our firsthand involvement ensured we understood the nuances of each step, allowing us to deliver reliable, high-quality results every time.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6">
                <Settings className="w-8 h-8 text-[#0007b5]" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Engineering First</h3>
              <p className="text-slate-600 leading-relaxed">
                We approach every project with an engineer's mindset—analyzing requirements, 
                optimizing designs, and selecting the right materials for your specific application.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Quality Obsessed</h3>
              <p className="text-slate-600 leading-relaxed">
                Every part undergoes rigorous quality checks. We maintain industry-leading standards 
                because we understand that your project's success depends on precision and reliability.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mb-6">
                <Zap className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Innovation Driven</h3>
              <p className="text-slate-600 leading-relaxed">
                We continuously explore new materials, techniques, and technologies to provide 
                cutting-edge solutions that push the boundaries of what's possible with 3D printing.
              </p>
            </div>
          </div>
        </section>

        {/* Expertise Areas with Visual Context */}
        <section className="mb-20">
          {/* Hero Image with Overlay and Text */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto relative">
            {/* Background Image */}
            <img 
              src="/parts_that_work_picture_2.jpg" 
              alt="Precision Engineering Equipment"
              className="w-full h-80 object-cover"
            />
            {/* Black Overlay (50% transparent = 50% opacity) */}
            <div className="absolute inset-0 bg-black opacity-50"></div>
            
            {/* Overlaid Text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Expertise</h2>
              <p className="text-xl text-white max-w-3xl mx-auto">
                Prototype, functional, or display: 3D printed parts that Work.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl p-8 border border-blue-100">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Prototype Development</h3>
              <p className="text-slate-700">
                Rapid iteration and testing with engineering analysis to optimize designs 
                for functionality, manufacturability, and production readiness.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-slate-50 rounded-xl p-8 border border-green-100">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Production Components</h3>
              <p className="text-slate-700">
                Load-bearing, functional parts with engineering validation and precise 
                material selection for specific performance and durability requirements.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-slate-50 rounded-xl p-8 border border-purple-100">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Cosmetic & Display</h3>
              <p className="text-slate-700">
                Smoothed, painted, and professionally finished components for presentation, 
                marketing materials, trade shows, and high-end visual applications.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-slate-50 rounded-xl p-8 border border-orange-100">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Engineering Consultation</h3>
              <p className="text-slate-700">
                Comprehensive design optimization, material selection guidance, and manufacturing 
                consultation based on years of real-world production experience.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-20 bg-slate-50 rounded-3xl p-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">Why Choose Us</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Our expertise spans every stage of 3D production—from design and prototyping to testing, revisions, and finishing—equipping us to support any need at any point. We understand how each phase integrates into the whole process, providing seamless, informed solutions that minimize risks and maximize efficiency for your projects.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Fast Turnaround Times</h3>
                  <p className="text-slate-600">Competitive pricing without compromising on quality or speed</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Expert Support</h3>
                  <p className="text-slate-600">Guidance throughout your entire project lifecycle</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Confidential Projects</h3>
                  <p className="text-slate-600">Secure handling with NDA protection for sensitive work</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Real-World Experience</h3>
                  <p className="text-slate-600">3,000+ lb. of production across every conceivable use case</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-slate-900 to-blue-900 rounded-3xl p-12 text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Experience the difference that engineering expertise makes. Get an instant quote 
            or contact us to discuss your project requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const quoteSection = document.querySelector('#instant-quote');
                  if (quoteSection) {
                    quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }}
              className="bg-white text-[#0007b5] hover:bg-blue-50 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Get Instant Quote
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="border border-blue-200 text-white hover:bg-blue-800 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Contact Us
            </button>
          </div>
        </section>

      </div>
    </SharedPageLayout>
  );
};

const ServicesRoute = () => {
  const navigate = useNavigate();
  
  // Handle hash-based navigation when component mounts or hash changes
  React.useEffect(() => {
    const hash = window.location.hash.substring(1); // Remove the # symbol
    if (hash) {
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          // Get element position and scroll with offset to show title
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 100; // 100px offset to show title and some space above
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);
  
  return (
    <SharedPageLayout currentPage="services">
      {/* Hero Banner - Modern Minimalistic Design */}
      <div className="relative bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-14 md:py-18">
            {/* Title with subtle accent */}
            <div className="max-w-4xl">
              <div className="inline-block mb-4">
                <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">Professional Services</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-6 page-title-uncage leading-tight">
                Our Services
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Engineering-driven 3D printing solutions backed by 8+ years of experience
              </p>
            </div>
            
            {/* Decorative element - subtle accent line */}
            <div className="mt-8 w-24 h-1 bg-gradient-to-r from-[#0007b5] to-blue-400"></div>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#0007b5] rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Core Services with Image */}
        <section className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> is your one-stop destination for all things 3D printing, leveraging our team's extensive engineering background to deliver tailored solutions that bridge ideas and execution. Whether you're a hobbyist, entrepreneur, or enterprise, our comprehensive offerings ensure you have the tools, expertise, and support to succeed.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                From initial concept to final production, our comprehensive services cover every stage of the 3D printing workflow. We combine cutting-edge technology with engineering expertise to deliver results that exceed expectations.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img 
                src="/services_illustration_2.png" 
                alt="Precision 3D Printing Workspace"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="space-y-6">
            {/* 3D File Printing Card */}
            <div id="3d-printing" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Upload className="w-8 h-8 text-[#0007b5]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">3D File Printing</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-4">
                    Professional analysis of your CAD files with material recommendations tailored to your specific use case and performance requirements.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Comprehensive file analysis and validation for optimal print quality</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Expert material selection based on mechanical properties and application requirements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Post-processing options including smoothing, painting, and finishing</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Fast turnaround times with production time estimates based on complexity</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 2D to 3D Conversion Card */}
            <div id="conversion" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-8 h-8 text-[#0007b5]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">2D to 3D Conversion</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-4">
                    Expert engineering services to transform technical drawings into optimized 3D models ready for production with proper tolerances.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Accurate conversion from 2D drawings, blueprints, and technical sketches</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Engineering-grade CAD modeling with proper dimensions and tolerances</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Design for manufacturability (DFM) optimization for 3D printing</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Multiple file format support (STEP, STL, OBJ, and more)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Design Services Card */}
            <div id="design" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-8 h-8 text-[#0007b5]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Design Services</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-4">
                    Complete design-to-production services with engineering expertise in functional, cosmetic, and load-bearing applications.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Custom product design from concept sketches or verbal descriptions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Functional prototyping and iterative design refinement</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Engineering analysis including stress testing and load calculations</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Production-ready files optimized for 3D printing technology</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Product Optimization Card */}
            <div id="optimization" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Settings className="w-8 h-8 text-[#0007b5]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Product Optimization</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-4">
                    Engineering analysis and optimization for maximum performance, durability, and manufacturing efficiency.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Weight reduction through topology optimization and lattice structures</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Material efficiency analysis to reduce costs while maintaining performance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Structural reinforcement for load-bearing and high-stress applications</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Print orientation and support structure optimization</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Realistic Renders Card - NEW */}
            <div id="renders" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Eye className="w-8 h-8 text-[#0007b5]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Model & Product Renders</h3>
                  <p className="text-lg text-slate-600 leading-relaxed mb-4">
                    Photorealistic visualization services to showcase your product designs before manufacturing, perfect for presentations and marketing.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>High-quality photorealistic renders with accurate materials and lighting</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Multiple viewing angles and environmental settings for comprehensive visualization</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Product animation and 360-degree rotation videos available</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#0007b5] mr-2">•</span>
                      <span>Marketing-ready images for presentations, websites, and promotional materials</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Experience Section with Visual Showcase */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">A Ton of Experience. Literally.</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our engineering team has hands-on experience printing over 3,000+ pounds of material 
              across every possible use case and application type.
            </p>
          </div>

          {/* Featured Image Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1732714552116-399f828fa355?w=600&q=80" 
                alt="Prototype Testing"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_threedworks/artifacts/qj9qlqjn_image%20%282%29%20edit.jpg" 
                alt="3D Printed Parts"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="/services_functional_picture_3.jpg" 
                alt="Precision Mechanical Components"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 bg-slate-50 rounded-3xl p-12">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">Prototype</div>
              <p className="text-slate-700 mb-4">
                Rapid iteration and testing phases for product development, functional validation, 
                and design verification with quick turnaround times.
              </p>
              <div className="text-sm text-slate-500">Applications: Concept validation, fit testing, form studies</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-green-600 mb-2">Cosmetic</div>
              <p className="text-slate-700 mb-4">
                Display-quality parts with smoothed surfaces, professional finishes, 
                painting preparation, and presentation-ready aesthetics.
              </p>
              <div className="text-sm text-slate-500">Applications: Trade shows, marketing materials, visual prototypes</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-purple-600 mb-2">Functional</div>
              <p className="text-slate-700 mb-4">
                Working components that integrate into assemblies, mechanisms, 
                and systems with proper dimensional accuracy and material properties.
              </p>
              <div className="text-sm text-slate-500">Applications: Jigs, fixtures, brackets, housings</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-red-600 mb-2">Load Bearing</div>
              <p className="text-slate-700 mb-4">
                Structural components designed to handle mechanical stress, 
                weight, and operational forces with engineering validation.
              </p>
              <div className="text-sm text-slate-500">Applications: Brackets, supports, structural elements</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-orange-600 mb-2">Post-Processed</div>
              <p className="text-slate-700 mb-4">
                Smoothed, sanded, and finished parts ready for painting, 
                coating, or wrapping applications with professional surface quality.
              </p>
              <div className="text-sm text-slate-500">Applications: Finished products, painted parts, wrapped components</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-2xl font-bold text-teal-600 mb-2">Production</div>
              <p className="text-slate-700 mb-4">
                End-use parts for manufacturing, tooling, and production applications 
                with consistent quality and dimensional accuracy.
              </p>
              <div className="text-sm text-slate-500">Applications: End-use parts, tooling, manufacturing aids</div>
            </div>
          </div>
        </section>

        {/* Why Choose Us with Image */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Why Choose <span className="brand-name"><span className="brand-name-semibold">CREATE 3D</span> <span className="brand-name-bold">Works</span></span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Engineering expertise meets manufacturing flexibility for results you can trust.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_threedworks/artifacts/oln56f8v_What%20Drives%20Us%20picture%202.png" 
                  alt="Professional 3D Printing Engineering Technology"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="space-y-8 order-1 md:order-2">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Engineering-First Approach</h3>
                <p className="text-slate-700">
                  Created by engineers who understand the technical challenges of 3D printing. 
                  We don't just print - we analyze, optimize, and engineer solutions.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Proven Track Record</h3>
                <p className="text-slate-700">
                  Over 3,000 lb. of material printed across every conceivable use case. 
                  From delicate prototypes to heavy-duty production components.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Complete Flexibility</h3>
                <p className="text-slate-700">
                  Whether you need rapid prototypes, cosmetic displays, functional parts, 
                  or production components - we have the experience to deliver.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Material Expertise</h3>
                <p className="text-slate-700">
                  Deep knowledge of material properties, applications, and limitations. 
                  We recommend the right material for your specific requirements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center bg-gradient-to-r from-slate-900 to-blue-900 rounded-3xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Get an instant quote and see why engineers choose <span className="brand-name"><span className="brand-name-semibold">CREATE 3D</span> <span className="brand-name-bold">Works</span></span> for their most challenging projects.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const quoteSection = document.querySelector('#instant-quote');
                  if (quoteSection) {
                    quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }}
              className="bg-white text-[#0007b5] hover:bg-blue-50 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Get Instant Quote
            </button>
            <button 
              onClick={() => navigate('/products')}
              className="border border-blue-200 text-white hover:bg-blue-500 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Browse Products
            </button>
          </div>
        </section>

      </div>
    </SharedPageLayout>
  );
};


const ContactRoute = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_BASE = BACKEND_URL;
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Thank you for contacting us! We will get back to you within 24 hours.' });
        
        // Track contact form submission in Google Analytics
        trackContactFormSubmission('Contact Page Form');
        
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again or email us directly.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again or email us directly.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SharedPageLayout currentPage="contact">
      {/* Hero Banner - Modern Minimalistic Design */}
      <div className="relative bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-14 md:py-18">
            {/* Title with subtle accent */}
            <div className="max-w-4xl">
              <div className="inline-block mb-4">
                <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">Get In Touch</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-6 page-title-uncage leading-tight">
                Contact Us
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Get in touch with our engineering team—we're here to help bring your project to life
              </p>
            </div>
            
            {/* Decorative element - subtle accent line */}
            <div className="mt-8 w-24 h-1 bg-gradient-to-r from-[#0007b5] to-blue-400"></div>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#0007b5] rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                <Mail className="w-8 h-8 text-[#0007b5]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Email Us</h3>
              <p className="text-slate-600 text-lg">hello@create3dworks.com</p>
              <p className="text-slate-500 text-sm mt-1">Response within 2 hours</p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                <Phone className="w-8 h-8 text-[#0007b5]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Call Us</h3>
              <p className="text-slate-600 text-lg">+1 (555) 123-4567</p>
              <p className="text-slate-500 text-sm mt-1">Mon-Fri 9AM-6PM EST</p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                <MapPin className="w-8 h-8 text-[#0007b5]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Visit Us</h3>
              <p className="text-slate-600 text-lg">590 Portland St - Unit 116<br />Dartmouth, NS B2W 6B7<br />Canada</p>
              <p className="text-slate-500 text-sm mt-1">Open Mon-Fri 9AM-5PM</p>
            </CardContent>
          </Card>
        </div>

        {/* Professional Message Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 text-center border border-blue-100">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">We're Here to Help</h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              Whether you have questions about our services, need technical guidance, or want to discuss 
              your project requirements, our engineering team is ready to provide expert support.
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-3xl mx-auto">
          <Card className="shadow-xl border-0 rounded-2xl">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-t-2xl">
              <CardTitle className="text-3xl font-bold text-slate-900">Send Us a Message</CardTitle>
              <p className="text-slate-600 mt-3 text-lg">Fill out the form below and we'll get back to you within 2 hours during business hours.</p>
            </CardHeader>
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    type="text"
                    placeholder="What is this regarding?"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <textarea
                    id="message"
                    rows="6"
                    placeholder="Tell us more about your project or inquiry..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {submitStatus && (
                  <div className={`p-4 rounded-lg ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-50 border border-green-200 text-green-800' 
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#0007b5] to-[#000690] hover:from-blue-700 hover:to-blue-800 text-white py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Trust Elements */}
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-[#0007b5] font-bold text-3xl mb-2">2 Hours</div>
              <div className="text-slate-600">Average Response Time</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-green-600 font-bold text-3xl mb-2">24/7</div>
              <div className="text-slate-600">Email Support Available</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-purple-600 font-bold text-3xl mb-2">100%</div>
              <div className="text-slate-600">Satisfaction Guaranteed</div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="max-w-4xl mx-auto mt-16 bg-slate-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Prefer Other Contact Methods?</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                <Mail className="w-5 h-5 mr-2 text-[#0007b5]" />
                Direct Email
              </h3>
              <p className="text-slate-700 mb-2">
                For detailed technical inquiries or file attachments:
              </p>
              <a href="mailto:hello@create3dworks.com" className="text-[#0007b5] hover:text-[#000690] font-medium">
                hello@create3dworks.com
              </a>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                <Phone className="w-5 h-5 mr-2 text-[#0007b5]" />
                Phone Support
              </h3>
              <p className="text-slate-700 mb-2">
                Call us during business hours:
              </p>
              <a href="tel:+15551234567" className="text-[#0007b5] hover:text-[#000690] font-medium">
                +1 (555) 123-4567
              </a>
              <p className="text-sm text-slate-500 mt-1">Mon-Fri 9AM-6PM EST</p>
            </div>
          </div>
        </div>

      </div>
    </SharedPageLayout>
  );
};

const FAQRoute = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: "What file formats do you accept?",
      answer: "We accept all major 3D file formats including STL, OBJ, 3MF, PLY, AMF, and STEP files. Our system can also work with native CAD files from popular software like SolidWorks, Fusion 360, and AutoCAD."
    },
    {
      question: "How long does it take to complete an order?",
      answer: "Turnaround times vary based on complexity and quantity. Simple prototypes typically take 1-3 business days, while complex or high-quantity orders may take 1-2 weeks. You'll receive an estimated delivery time with your quote."
    },
    {
      question: "What materials do you offer?",
      answer: "We offer a wide range of materials including PLA, ABS, PETG, TPU, Wood-filled, Metal-filled filaments for FDM printing, and various resins for SLA printing. We also provide specialty materials for specific applications."
    },
    {
      question: "Do you offer design services?",
      answer: "Yes! Our experienced design team can help with everything from concept development to production-ready designs. We offer 2D to 3D conversion, design optimization, and custom engineering services."
    },
    {
      question: "What's your quality guarantee?",
      answer: "We maintain strict quality standards and inspect every part before shipping. If you're not satisfied with the quality, we'll reprint your order at no additional cost or provide a full refund."
    },
    {
      question: "How do you handle confidential projects?",
      answer: "We take intellectual property seriously. All projects are covered by strict confidentiality agreements, and we can sign additional NDAs as needed. Your files are securely stored and never shared."
    },
    {
      question: "What are your payment terms?",
      answer: "We accept all major credit cards and PayPal. Payment is required before production begins. For large orders or established business customers, we may offer net payment terms."
    },
    {
      question: "Do you ship internationally?",
      answer: "Currently, we ship within the United States and Canada. International shipping options are being developed and will be available soon."
    },
    {
      question: "Can I track my order?",
      answer: "Yes! You'll receive a tracking number once your order ships. You can also check your order status anytime by logging into your account and viewing your order history."
    },
    {
      question: "What if I need to make changes to my order?",
      answer: "Changes can be made before production begins. Once printing has started, changes may not be possible or may incur additional costs. Contact us as soon as possible if you need modifications."
    }
  ];

  return (
    <SharedPageLayout currentPage="faq">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-slate-600">
            Find answers to common questions about our 3D printing services
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-slate-200 rounded-lg">
              <button
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-slate-50 transition-colors"
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              >
                <span className="font-medium text-slate-900">{faq.question}</span>
                <ChevronDown className={`w-5 h-5 text-slate-500 transform transition-transform ${
                  openFaq === index ? 'rotate-180' : ''
                }`} />
              </button>
              {openFaq === index && (
                <div className="px-6 pb-4">
                  <p className="text-slate-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-blue-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-semibold text-blue-900 mb-4">Still Have Questions?</h2>
          <p className="text-blue-800 mb-6">
            Can't find what you're looking for? Our team is here to help.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Contact Us
          </button>
        </div>
      </div>
    </SharedPageLayout>
  );
};

const TermsRoute = () => {
  return (
    <SharedPageLayout currentPage="terms">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Terms of Service</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-700 mb-4">
              By accessing and using <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> services, you accept and agree to be bound by the terms 
              and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Services</h2>
            <p className="text-slate-700 mb-4">
              <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> provides 3D printing, design, and manufacturing services. We reserve the right to 
              modify or discontinue any service at any time without notice.
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>3D printing and prototyping services</li>
              <li>Design and engineering consulting</li>
              <li>File conversion and optimization</li>
              <li>Manufacturing and production support</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. User Responsibilities</h2>
            <p className="text-slate-700 mb-4">Users are responsible for:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Ensuring they have legal rights to all submitted designs and files</li>
              <li>Providing accurate project specifications and requirements</li>
              <li>Complying with all applicable laws and regulations</li>
              <li>Not submitting designs for illegal, harmful, or offensive items</li>
              <li>Respecting intellectual property rights of others</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Intellectual Property</h2>
            <p className="text-slate-700 mb-4">
              You retain all rights to your original designs and files. <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> does not claim ownership 
              of customer-submitted content. However, by submitting files, you grant us a limited license to 
              process and manufacture your designs as part of our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Payment Terms</h2>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>All prices are quoted in USD unless otherwise specified</li>
              <li>Payment is required before production begins</li>
              <li>We accept major credit cards and PayPal</li>
              <li>Pricing may vary based on material costs and project complexity</li>
              <li>Rush orders may incur additional fees</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibent text-slate-900 mb-4">6. Quality and Delivery</h2>
            <p className="text-slate-700 mb-4">
              We strive to deliver high-quality products within the specified timeframe. However:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Delivery times are estimates and may vary due to production complexity</li>
              <li>We inspect all products before shipping but cannot guarantee perfection</li>
              <li>Customer approval may be required for certain design modifications</li>
              <li>We will remake defective parts at no additional cost when fault lies with our production</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Confidentiality</h2>
            <p className="text-slate-700 mb-4">
              We understand the importance of protecting your intellectual property. All customer files and 
              project information are treated as confidential and are not shared with third parties without 
              explicit consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-slate-700 mb-4">
              <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span>' liability is limited to the cost of the specific service provided. We are not 
              responsible for indirect, consequential, or incidental damages arising from our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Contact Information</h2>
            <p className="text-slate-700 mb-4">
              For questions about these Terms of Service, please contact us through our website or customer service.
            </p>
          </section>

          <div className="bg-slate-100 rounded-lg p-6 mt-8">
            <p className="text-slate-700 text-sm">
              These terms are subject to change without notice. Continued use of our services constitutes 
              acceptance of any modifications to these terms.
            </p>
          </div>
        </div>
      </div>
    </SharedPageLayout>
  );
};
// Policy Routes
const TermsOfServiceRoute = () => {
  return (
    <SharedPageLayout currentPage="terms-of-service">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Terms of Service</h1>
          <p className="text-slate-600 mb-8">Last updated: January 2025</p>
          <div dangerouslySetInnerHTML={{ __html: termsOfService }} />
        </div>
      </div>
    </SharedPageLayout>
  );
};

const ShippingReturnsRoute = () => {
  return (
    <SharedPageLayout currentPage="shipping-returns">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Shipping & Returns</h1>
          <p className="text-slate-600 mb-8">Last updated: January 2025</p>
          <div dangerouslySetInnerHTML={{ __html: shippingReturns }} />
        </div>
      </div>
    </SharedPageLayout>
  );
};

const LicensingPolicyRoute = () => {
  return (
    <SharedPageLayout currentPage="licensing-policy">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Licensing Policy</h1>
          <p className="text-slate-600 mb-8">Last updated: January 2025</p>
          <div dangerouslySetInnerHTML={{ __html: licensingPolicy }} />
        </div>
      </div>
    </SharedPageLayout>
  );
};

const PrivacyPolicyRoute = () => {
  return (
    <SharedPageLayout currentPage="privacy-policy">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Privacy Policy</h1>
          <p className="text-slate-600 mb-8">Last updated: January 2025</p>
          <div dangerouslySetInnerHTML={{ __html: privacyPolicy }} />
        </div>
      </div>
    </SharedPageLayout>
  );
};

const FileFormatsRoute = () => {
  const fileFormats = [
    {
      format: "STL",
      description: "Standard format for 3D printing, contains mesh geometry",
      compatibility: "Excellent",
      recommended: true,
      details: "Most common 3D printing format. Works with all our printing technologies."
    },
    {
      format: "OBJ", 
      description: "Simple 3D geometry format with optional texture support",
      compatibility: "Excellent",
      recommended: true,
      details: "Great for models with textures or complex geometries."
    },
    {
      format: "3MF",
      description: "Microsoft 3D Manufacturing Format with rich metadata",
      compatibility: "Excellent", 
      recommended: true,
      details: "Modern format that preserves materials, colors, and print settings."
    },
    {
      format: "PLY",
      description: "Polygon file format for storing 3D scan data",
      compatibility: "Good",
      recommended: false,
      details: "Useful for scanned objects and research applications."
    },
    {
      format: "AMF",
      description: "Additive Manufacturing File format with color support",
      compatibility: "Good",
      recommended: false,
      details: "Advanced format supporting multiple materials and colors."
    },
    {
      format: "STEP/STP",
      description: "CAD format preserving parametric geometry",
      compatibility: "Good",
      recommended: true,
      details: "Best for engineering parts requiring precise dimensions."
    }
  ];

  return (
    <SharedPageLayout currentPage="file-formats">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Supported File Formats</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            We support all major 3D file formats to ensure compatibility with your design workflow
          </p>
        </div>

        {/* File Formats Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-12">
          {fileFormats.map((format, index) => (
            <div key={index} className={`border rounded-lg p-6 ${
              format.recommended ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-white'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-900">.{format.format}</h3>
                {format.recommended && (
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                    Recommended
                  </span>
                )}
              </div>
              <p className="text-slate-700 mb-4">{format.description}</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Compatibility:</span>
                  <span className={`text-sm font-medium ${
                    format.compatibility === 'Excellent' ? 'text-green-600' : 'text-[#0007b5]'
                  }`}>
                    {format.compatibility}
                  </span>
                </div>
                <p className="text-sm text-slate-600">{format.details}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Guidelines */}
        <div className="bg-slate-50 rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">File Preparation Guidelines</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">✅ Best Practices</h3>
              <ul className="space-y-2 text-slate-700">
                <li>• Use STL or 3MF for best compatibility</li>
                <li>• Ensure models are manifold (watertight)</li>
                <li>• Include proper wall thickness (min 0.8mm)</li>
                <li>• Orient models for optimal printing</li>
                <li>• Check for intersecting geometries</li>
                <li>• Keep file sizes reasonable (&lt;50MB)</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">⚠️ Common Issues</h3>
              <ul className="space-y-2 text-slate-700">
                <li>• Non-manifold geometry</li>
                <li>• Holes or gaps in mesh</li>
                <li>• Inverted normals</li>
                <li>• Too thin walls (&lt;0.4mm)</li>
                <li>• Unsupported overhangs</li>
                <li>• Corrupted or incomplete files</li>
              </ul>
            </div>
          </div>
        </div>

        {/* File Size Limits */}
        <div className="bg-blue-50 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">File Size & Upload Limits</h3>
          <div className="grid md:grid-cols-3 gap-4 text-center">
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">50MB</div>
              <div className="text-sm text-slate-600">Max file size</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">10</div>
              <div className="text-sm text-slate-600">Files per upload</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">24/7</div>
              <div className="text-sm text-slate-600">Upload availability</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-slate-900 mb-4">Ready to Upload Your Files?</h2>
          <p className="text-slate-600 mb-6">
            Start your project today and get an instant quote for your 3D printing needs
          </p>
          <a
            href="/"
            className="inline-block bg-[#0007b5] hover:bg-[#000690] text-white px-8 py-3 rounded-lg font-medium transition-colors"
          >
            Get Started
          </a>
        </div>
      </div>
    </SharedPageLayout>
  );
};
const MaterialGuideRoute = () => {
  const navigate = useNavigate();
  
  return (
    <SharedPageLayout currentPage="material-guide">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">Material Guide</h1>
        <p className="text-lg text-slate-600 mb-8">
          This guide covers key materials, including their strengths, weaknesses, pros and cons for various applications, 
          and documented mechanical properties from industry testing (e.g., tensile strength, impact resistance, heat deflection temperature or HDT). 
          Data is sourced from reliable benchmarks like UltiMaker, Formlabs, and filament manufacturers. We focus on FDM/FFF printing, 
          where these materials excel. <button 
            onClick={() => navigate('/contact')} 
            className="font-semibold text-[#0007b5] hover:text-blue-700 underline cursor-pointer"
          >
            Consult our team for custom advice or material samples.
          </button>
        </p>

        <div className="space-y-12">
          {/* PLA Section */}
          <section className="border-l-4 border-green-500 pl-6 bg-green-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">PLA (Polylactic Acid) and Variants</h2>
            <p className="text-slate-700 mb-4">
              PLA is a biodegradable thermoplastic derived from renewable resources like cornstarch, making it the most beginner-friendly filament.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Easy to print with low warping and no need for an enclosure or heated bed</li>
                  <li>• Affordable, odorless (mild sweet smell), and non-toxic (food-safe in raw form)</li>
                  <li>• Excellent detail and surface finish; eco-friendly</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Brittle with low impact resistance; deforms under heat or UV exposure</li>
                  <li>• Not suitable for high-stress or outdoor use</li>
                  <li>• Requires industrial composting to biodegrade</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">Prototypes, decorative items, figurines, hobby projects, and low-stress parts like cookie cutters.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Functional parts in heat (e.g., car interiors) or mechanical loads.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties (Standard PLA)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 50-60 MPa</div>
                <div><strong>Impact Resistance:</strong> Low</div>
                <div><strong>Elongation:</strong> 3-5%</div>
                <div><strong>HDT:</strong> ~55-60°C</div>
                <div><strong>Layer Adhesion:</strong> Medium</div>
                <div><strong>Chemical Resistance:</strong> Low</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">PLA+ (Tough PLA)</h4>
                <p className="text-sm text-slate-700">Enhanced impact resistance (tensile 60-65 MPa) and heat tolerance (~60-70°C); Less brittle but slight stringing. <strong>Uses:</strong> Functional prototypes.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">Silk PLA</h4>
                <p className="text-sm text-slate-700">Glossy, aesthetic finish that hides layer lines; Softer/weaker (tensile 30-50 MPa). <strong>Uses:</strong> Decorative prints.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">Wood/Metal-Filled PLA</h4>
                <p className="text-sm text-slate-700">Textured appearance; Sandable/polishable but abrasive (requires hardened nozzle), brittle. <strong>Uses:</strong> Artistic models.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">CF PLA (Carbon Fiber PLA)</h4>
                <p className="text-sm text-slate-700 mb-2">Reinforced with 10-15% carbon fibers for added rigidity. Higher stiffness, strength, and matte finish that masks layers. Abrasive (needs hardened nozzle), potentially brittle, higher cost.</p>
                <p className="text-xs text-slate-600"><strong>Mechanical:</strong> Tensile 50-70 MPa, HDT ~55-65°C, elongation 2-4%. <strong>Uses:</strong> Functional prototypes, gears, brackets, load-bearing parts.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">HT PLA (High Temperature PLA)</h4>
                <p className="text-sm text-slate-700 mb-2">Formulated for better heat resistance, often requiring annealing. Improved HDT and impact (3x standard PLA in some tests), maintains print ease. May have lower Z-axis strength (~43% less in some variants).</p>
                <p className="text-xs text-slate-600"><strong>Mechanical:</strong> Tensile 45-55 MPa, HDT ~70-100°C (post-annealing at 100°C for 30 min), elongation 3-5%. <strong>Uses:</strong> Heat-exposed prototypes, enclosures, functional parts.</p>
              </div>
            </div>
          </section>

          {/* PETG Section */}
          <section className="border-l-4 border-blue-500 pl-6 bg-blue-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">PETG (Polyethylene Terephthalate Glycol) and Variants</h2>
            <p className="text-slate-700 mb-4">
              PETG is a durable, glycol-modified version of PET (bottle plastic), offering a balance of strength and ease.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Tough with good flexibility and impact absorption</li>
                  <li>• Chemically and UV resistant</li>
                  <li>• Minimal warping, recyclable, and food-safe</li>
                  <li>• Strong layer adhesion for watertight prints</li>
                  <li>• Versatile for indoor/outdoor use; low odor</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Prone to stringing and oozing (needs tuning)</li>
                  <li>• Can damage brass nozzles over time</li>
                  <li>• Moderately moisture-sensitive</li>
                  <li>• Sticks aggressively to print beds (use release agents)</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">Functional parts like brackets, enclosures, containers, prosthetics, and vase-mode prints.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Extreme heat applications (e.g., engine parts) or abrasive environments.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties (Standard PETG)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 40-60 MPa</div>
                <div><strong>Impact Resistance:</strong> Medium</div>
                <div><strong>Elongation:</strong> 20-30%</div>
                <div><strong>HDT:</strong> ~70-80°C</div>
                <div><strong>Layer Adhesion:</strong> High</div>
                <div><strong>Chemical Resistance:</strong> High</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">PCTG</h4>
                <p className="text-sm text-slate-700">Higher impact (20% more than PETG); More flexible, less warping. Similar stringing. <strong>Uses:</strong> Impact-prone parts.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">CPE/CPE+</h4>
                <p className="text-sm text-slate-700">Similar to PETG but higher heat (~100°C); Stronger adhesion but more expensive. <strong>Uses:</strong> Electronics housings.</p>
              </div>
            </div>
          </section>

          {/* ABS Section */}
          <section className="border-l-4 border-orange-500 pl-6 bg-orange-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">ABS (Acrylonitrile Butadiene Styrene) and Variants</h2>
            <p className="text-slate-700 mb-4">
              ABS is a tough engineering plastic used in LEGO and automotive parts, known for durability.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• High impact and heat resistance</li>
                  <li>• Flexible without brittleness</li>
                  <li>• Can be smoothed with acetone for glossy finish</li>
                  <li>• Affordable and gluable</li>
                  <li>• Good for post-processing like painting or sanding</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Significant warping (requires heated bed/enclosure)</li>
                  <li>• Emits strong fumes (needs ventilation)</li>
                  <li>• UV-unstable (degrades outdoors)</li>
                  <li>• Not food-safe</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">Functional prototypes, tool handles, enclosures, drone frames, and automotive clips.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Food contact or prolonged sun exposure without coatings.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties (Standard ABS)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 34-40 MPa</div>
                <div><strong>Impact Resistance:</strong> High</div>
                <div><strong>Elongation:</strong> 20-30%</div>
                <div><strong>HDT:</strong> ~98-100°C</div>
                <div><strong>Layer Adhesion:</strong> Medium</div>
                <div><strong>Chemical Resistance:</strong> Medium</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">ABS+</h4>
                <p className="text-sm text-slate-700">Reduced warping (~50% less) and odor; Flame-retardant options. Similar fumes. <strong>Uses:</strong> Safety-critical parts.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">ASA</h4>
                <p className="text-sm text-slate-700">UV/weather-resistant; Matte finish, milder odor. Slightly higher cost. HDT ~105°C. <strong>Uses:</strong> Outdoor applications.</p>
              </div>
            </div>
          </section>

          {/* TPU Section */}
          <section className="border-l-4 border-purple-500 pl-6 bg-purple-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">TPU (Thermoplastic Polyurethane)</h2>
            <p className="text-slate-700 mb-4">
              TPU is a flexible, rubber-like elastomer ideal for elastic parts.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Highly flexible and stretchable</li>
                  <li>• Excellent impact and abrasion resistance</li>
                  <li>• Durable with vibration dampening</li>
                  <li>• Low warping and chemical resistance</li>
                  <li>• Good for skin-safe applications</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Difficult to print (slow speeds, direct-drive extruder needed)</li>
                  <li>• Prone to stringing and jams</li>
                  <li>• Highly moisture-sensitive (causes popping)</li>
                  <li>• Soft, deforms under weight</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">Phone cases, gaskets, tires, grips, wearables, and bumpers.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Rigid structural parts or high-precision prints.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 20-40 MPa</div>
                <div><strong>Impact Resistance:</strong> Very high</div>
                <div><strong>Elongation:</strong> >500%</div>
                <div><strong>HDT:</strong> ~60-80°C</div>
                <div><strong>Shore Hardness:</strong> 85A-98A</div>
                <div><strong>Chemical Resistance:</strong> High</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">TPU 85A (Ultra-Flexible)</h4>
                <p className="text-sm text-slate-700">Very soft; High stretch. Prints slowly (10-20 mm/s). <strong>Uses:</strong> Seals.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">TPU 64D</h4>
                <p className="text-sm text-slate-700">Semi-rigid; Easier to print. Less flexible. <strong>Uses:</strong> Structural flex parts.</p>
              </div>
            </div>
          </section>

          {/* PC Section */}
          <section className="border-l-4 border-indigo-500 pl-6 bg-indigo-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">PC (Polycarbonate)</h2>
            <p className="text-slate-700 mb-4">
              PC is a high-performance engineering plastic known for transparency and toughness.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Extremely strong, stiff, and heat-resistant</li>
                  <li>• Lightweight in composites</li>
                  <li>• Impact-resistant (bulletproof glass material)</li>
                  <li>• Transparent options</li>
                  <li>• Good for high-temp and mechanical stress</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Challenging to print (high warping, needs enclosure and high temps ~270-290°C)</li>
                  <li>• Expensive</li>
                  <li>• Abrasive in filled variants (requires hardened nozzle)</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">High-heat prototypes, molds, prosthetics, and engineering parts.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Beginner prints or low-temp environments.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 50-70 MPa</div>
                <div><strong>Impact Resistance:</strong> Very high</div>
                <div><strong>Elongation:</strong> 50-100%</div>
                <div><strong>HDT:</strong> ~150°C</div>
                <div><strong>Layer Adhesion:</strong> Medium</div>
                <div><strong>Chemical Resistance:</strong> High</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">PC-CF/PC-GF (Carbon/Glass Fiber)</h4>
                <p className="text-sm text-slate-700">Stiffer, less warping; Heat ~120°C. Brittle in CF. <strong>Uses:</strong> Aerospace-like parts.</p>
              </div>
            </div>
          </section>

          {/* PA Section */}
          <section className="border-l-4 border-teal-500 pl-6 bg-teal-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">PA (Polyamide/Nylon)</h2>
            <p className="text-slate-700 mb-4">
              PA, or Nylon, is a strong, semi-flexible synthetic polymer used in textiles and gears.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Exceptional toughness and impact resistance</li>
                  <li>• Self-lubricating with low friction</li>
                  <li>• Heat and chemical resistant</li>
                  <li>• Bends without breaking</li>
                  <li>• Ideal for wear-resistant applications</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Extremely hygroscopic (must dry before printing)</li>
                  <li>• Warps easily (needs enclosure)</li>
                  <li>• Difficult to print (high temps ~240-270°C)</li>
                  <li>• Dimensional instability in humidity</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">Gears, bushings, tools, prosthetics, drone propellers, and mechanical parts.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Humid environments without sealing or non-functional prints.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 40-80 MPa</div>
                <div><strong>Impact Resistance:</strong> Very high</div>
                <div><strong>Elongation:</strong> 20-50%</div>
                <div><strong>HDT:</strong> ~50-100°C</div>
                <div><strong>Layer Adhesion:</strong> High</div>
                <div><strong>Chemical Resistance:</strong> High</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">PA6/PA66</h4>
                <p className="text-sm text-slate-700">Very tough; High impact. High moisture/warp. <strong>Uses:</strong> Heavy-duty parts.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">PA12</h4>
                <p className="text-sm text-slate-700">Easier print, lower moisture; Balanced. Slightly less strong. <strong>Uses:</strong> General engineering.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">CF/GF Nylon</h4>
                <p className="text-sm text-slate-700">Stiffer, heat ~120°C+; Impact-resistant. Abrasive. <strong>Uses:</strong> Structural components.</p>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-3">Ready to Choose Your Material?</h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Our team can help you select the perfect material for your project based on your specific requirements and application.
          </p>
          <a 
            href="/" 
            className="inline-block bg-white text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl"
          >
            Start Your Project
          </a>
        </div>
      </div>
    </SharedPageLayout>
  );
};

const QualityStandardsRoute = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="bg-gradient-to-r from-[#0007b5] to-[#000690] bg-clip-text text-transparent">
                <BrandName size="md" />
              </h1>
            </button>
            <button onClick={() => navigate('/')} className="text-slate-600 hover:text-slate-800 transition-colors">← Back to Home</button>
          </div>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Quality Standards</h1>
        <div className="space-y-8">
          <section className="bg-green-50 rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-green-900 mb-4">Our Quality Promise</h2>
            <p className="text-green-800">Every part we produce meets or exceeds industry standards for precision, surface finish, and dimensional accuracy.</p>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Precision Standards</h2>
            <ul className="space-y-2 text-slate-700">
              <li>• Dimensional accuracy: ±0.1mm for critical features</li>
              <li>• Layer height: 0.1mm to 0.3mm options</li>
              <li>• Surface finish: Ra 1.6-6.3 μm depending on process</li>
              <li>• Material consistency: &lt;2% variance in properties</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Quality Control Process</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Pre-Production</h3>
                <p className="text-slate-700">File analysis, printability assessment, and optimization recommendations.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">During Production</h3>
                <p className="text-slate-700">Real-time monitoring, temperature control, and process verification.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Post-Production</h3>
                <p className="text-slate-700">Visual inspection, dimensional verification, and surface finish assessment.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Packaging</h3>
                <p className="text-slate-700">Protective packaging and detailed quality documentation included.</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const ShippingInfoRoute = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="bg-gradient-to-r from-[#0007b5] to-[#000690] bg-clip-text text-transparent">
                <BrandName size="md" />
              </h1>
            </button>
            <button onClick={() => navigate('/')} className="text-slate-600 hover:text-slate-800 transition-colors">← Back to Home</button>
          </div>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Shipping & Returns</h1>
        <div className="space-y-8">
          <section className="bg-blue-50 rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-blue-900 mb-4">Shipping Options</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Standard Shipping</h3>
                <p className="text-slate-700 mb-2">5-7 business days • FREE on orders over $50</p>
                <p className="text-sm text-slate-600">Perfect for non-urgent orders</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Express Shipping</h3>
                <p className="text-slate-700 mb-2">2-3 business days • $15.99</p>
                <p className="text-sm text-slate-600">When you need it fast</p>
              </div>
            </div>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Coverage Areas</h2>
            <ul className="space-y-2 text-slate-700">
              <li>• United States: All 50 states</li>
              <li>• Canada: All provinces and territories</li>
              <li>• International shipping: Coming soon</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Packaging & Handling</h2>
            <p className="text-slate-700 mb-4">
              All orders are carefully packaged with protective materials to ensure your parts arrive in perfect condition. 
              We use recyclable packaging materials whenever possible.
            </p>
            <ul className="space-y-2 text-slate-700">
              <li>• Bubble wrap protection for delicate parts</li>
              <li>• Rigid boxes for dimensional stability</li>
              <li>• Detailed packing slips included</li>
              <li>• Tracking information provided</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

// Main App Content with Firebase Authentication
const AppContent = () => {
  const navigate = useNavigate();
  const { cart, addToCart, clearCart } = useCart();
  const { user, isAuthenticated, loginWithGoogle, logout, loading, getIdToken } = useAuth();
  const { formatPrice } = useCurrency();
  
  const [activeTab, setActiveTab] = useState("upload");
  const [quoteData, setQuoteData] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [formData, setFormData] = useState({
    file: null,
    material: "",
    color: "Any",
    customColor: "",
    quality: "",
    strength: "",
    smoothing: "",
    contact_email: "",
    notes: "",
    units: "mm",  // Default to millimeters
    country_code: "CA",  // Default shipping to Canada (domestic)
    postal_code: "",  // Optional postal/zip code
    existing_quote_id: null,  // For preserving file when editing quote
    preserved_filename: null  // Filename of preserved file when editing
  });

  // Handle hash-based navigation on page load
  React.useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 100;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  // Check for payment status on page load
  React.useEffect(() => {
    // Wait for Firebase auth to finish loading
    if (loading) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentParam = urlParams.get('payment');

    if (sessionId && paymentParam === 'success') {
      // User returned from successful Stripe checkout
      if (isAuthenticated) {
        setPaymentStatus('checking');
        pollPaymentStatus(sessionId);
      } else {
        // User is not authenticated, might have been logged out during payment
        setPaymentError('Please log in to check your payment status.');
      }
    } else if (paymentParam === 'cancelled') {
      setPaymentError('Payment was cancelled. You can try again anytime.');
    }
  }, [loading, isAuthenticated]); // Add dependencies to wait for auth state

  // Function to poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 3000; // 3 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setPaymentError('Payment verification timed out. Please check your email or profile for order status.');
      return;
    }

    try {
      // Get Firebase ID token for authenticated request
      const idToken = await getIdToken();
      if (!idToken) {
        // If we can't get the token, wait a bit and retry (in case auth is still loading)
        if (attempts < 3) {
          console.log(`Waiting for authentication... attempt ${attempts + 1}`);
          setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
          return;
        } else {
          setPaymentStatus('error');
          setPaymentError('Unable to verify authentication. Please log in and check your profile for order status.');
          return;
        }
      }

      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          setPaymentError(null);
          
          // Track order completion in Google Analytics
          if (data.order_id && data.total_amount) {
            trackOrderCreation({
              order_id: data.order_id,
              total_price: data.total_amount,
              quote_id: data.quote_id,
              item_name: data.item_name || 'Print Order'
            });
          }
          
          // Clear the cart after successful payment
          clearCart();
          console.log('Payment successful - cart cleared');
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        } else if (data.status === 'expired') {
          setPaymentStatus('failed');
          setPaymentError('Payment session expired. Please try again.');
          return;
        }

        // If payment is still pending, continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        throw new Error('Failed to check payment status');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setPaymentError('Error checking payment status. Please check your profile for order updates.');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    // Clear preserved filename when a new file is uploaded
    setFormData({ ...formData, file, preserved_filename: null });
  };

  const scrollToQuoteSection = () => {
    setActiveTab("upload");
    setTimeout(() => {
      const quoteSection = document.querySelector('[data-section="quote-interface"]');
      if (quoteSection) {
        quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleFooterLinkClick = (section) => {
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToQuoteAndSetTab = (tabName) => {
    setActiveTab(tabName);
    setTimeout(() => {
      const quoteSection = document.querySelector('[data-section="quote-interface"]');
      if (quoteSection) {
        quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const navigateToPage = (page) => {
    navigate(`/${page}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToProduct = (product) => {
    const slug = createProductSlug(product.manufacturer, product.name);
    navigate(`/product/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToProducts = () => {
    navigate('/products');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToCart = () => {
    navigate('/cart');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that either a file is uploaded OR we have an existing_quote_id
    if (!formData.file && !formData.existing_quote_id) {
      alert('Please upload a 3D model file');
      return;
    }
    
    setLoadingQuote(true);
    
    try {
      const formDataObj = new FormData();
      
      // Only append file if a new one is uploaded
      if (formData.file) {
        formDataObj.append('file', formData.file);
      }
      
      // If editing an existing quote, include the quote_id
      if (formData.existing_quote_id) {
        formDataObj.append('existing_quote_id', formData.existing_quote_id);
      }
      
      formDataObj.append('material', formData.material);
      formDataObj.append('color', formData.color === 'Other' ? formData.customColor : formData.color);
      formDataObj.append('quality', formData.quality);
      formDataObj.append('strength', formData.strength);
      formDataObj.append('smoothing', formData.smoothing);
      formDataObj.append('contact_email', formData.contact_email);
      formDataObj.append('units', formData.units);  // Add units to submission
      formDataObj.append('country_code', formData.country_code);  // Add shipping destination
      if (formData.postal_code) formDataObj.append('postal_code', formData.postal_code);  // Add postal code if provided
      if (formData.notes) formDataObj.append('notes', formData.notes);
      
      // Add dimensions for accurate shipping calculation (if available from 3D analysis)
      if (formData.analysisData && formData.analysisData.dimensions) {
        formDataObj.append('dimension_x', formData.analysisData.dimensions.x);
        formDataObj.append('dimension_y', formData.analysisData.dimensions.y);
        formDataObj.append('dimension_z', formData.analysisData.dimensions.z);
        console.log('Sending dimensions for shipping calculation:', formData.analysisData.dimensions);
      }

      const response = await fetch(`${API}/quote/3d-print`, {
        method: 'POST',
        body: formDataObj,
      });

      if (response.ok) {
        const result = await response.json();
        setQuoteData(result);
        setActiveTab("quote");
        setOrderSuccess(null); // Clear any previous order success messages
        
        // Track quote submission in Google Analytics
        trackQuoteSubmission({
          material: formData.material,
          quality: formData.quality,
          estimated_price: result.final_price
        });
        
        // Scroll to the instant quote section to show results
        setTimeout(() => {
          const quoteSection = document.querySelector('#instant-quote');
          if (quoteSection) {
            quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        alert('Error submitting quote request');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting quote request');
    } finally {
      setLoadingQuote(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (!quoteData) {
      alert('No quote data available');
      return;
    }

    setLoadingOrder(true);
    
    try {
      // Get filename from formData (the uploaded file name)
      const filename = formData.file ? formData.file.name : quoteData.filename || 'Custom 3D Model';
      
      // Format description with all print settings from formData (user selections)
      const description = [
        `📄 File: ${filename}`,
        `🎨 Material: ${formData.material} - ${formData.color === 'Other' ? formData.customColor : formData.color}`,
        `⚙️ Quality: ${formData.quality}`,
        `💪 Strength: ${formData.strength}`,
        `✨ Smoothing: ${formData.smoothing}`
      ].join('\n');
      
      const cartItem = {
        id: `quote-${quoteData.quote_id}`, // Unique ID for cart item
        quote_id: quoteData.quote_id,
        type: 'print_quote',
        name: `Print Order - Quote ${quoteData.quote_id.substring(0, 8)}`,
        description: description,
        price: quoteData.price_breakdown.print_cost + quoteData.price_breakdown.smoothing_cost, // Price without shipping
        image: 'https://img.icons8.com/color/150/000000/3d-printer.png', // 3D printer icon
        image_url: 'https://img.icons8.com/color/150/000000/3d-printer.png', // Cart uses image_url
        category: 'Custom 3D Print',
        manufacturer: 'On-Demand Print',
        in_stock: true,
        quantity: 1, // Default quantity
        // Store all quote data for order creation
        quote_data: {
          quote_id: quoteData.quote_id,
          filename: filename,
          material: formData.material,
          color: formData.color === 'Other' ? formData.customColor : formData.color,
          quality: formData.quality,
          strength: formData.strength,
          smoothing: formData.smoothing,
          volume: quoteData.volume,
          surface_area: quoteData.surface_area,
          complexity_score: quoteData.complexity_score,
          price_breakdown: quoteData.price_breakdown,
          estimated_time: quoteData.estimated_time,
          delivery_weeks: quoteData.delivery_weeks,
          production_time: quoteData.production_time,
          contact_email: formData.contact_email,
          units: formData.units,
          shipping_country_code: formData.country_code
        }
      };
      
      // Add to cart
      addToCart(cartItem);
      
      // Show success message and redirect to cart
      alert(`✓ Quote added to cart!\n\nYou can now proceed to checkout or continue shopping.`);
      
      // Navigate to cart
      navigate('/cart');
      
    } catch (error) {
      console.error('Error adding quote to cart:', error);
      alert('Error adding quote to cart. Please try again.');
    } finally {
      setLoadingOrder(false);
    }
  };

  const handlePayment = async (orderId) => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    setLoadingPayment(true);
    setPaymentError(null);

    try {
      // Get Firebase ID token
      const idToken = await getIdToken();
      if (!idToken) {
        setPaymentError('Authentication error. Please log in again.');
        setShowLoginModal(true);
        return;
      }

      // Get current URL for success and cancel URLs
      const currentUrl = window.location.origin + window.location.pathname;
      const successUrl = `${currentUrl}?payment=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}?payment=cancelled`;

      // Get backend URL
      const backendUrl = BACKEND_URL;
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      const response = await fetch(`${apiUrl}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
        body: JSON.stringify({
          order_id: orderId,
          success_url: successUrl,
          cancel_url: cancelUrl
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Redirect to Stripe checkout
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        const errorData = await response.json();
        if (response.status === 401) {
          setShowLoginModal(true);
          setPaymentError('Please log in to proceed with payment');
        } else {
          setPaymentError(`Payment error: ${errorData.detail || 'Please try again'}`);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError('Error creating payment. Please try again.');
    } finally {
      setLoadingPayment(false);
    }
  };

  const handleServiceRequest = async (serviceType, formData, file = null) => {
    try {
      let endpoint;
      let body;
      let headers = {};

      if (serviceType === "2D to 3D Conversion" || serviceType === "Product Optimization") {
        if (!file) {
          alert('File upload is required for this service');
          return;
        }
        endpoint = `${API}/request/service-with-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', formData.name);
        body.append('contact_email', formData.email);
        body.append('description', formData.description);
        body.append('file', file);
      } else if (serviceType === "Design Service") {
        endpoint = `${API}/request/service-optional-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', formData.name);
        body.append('contact_email', formData.email);
        body.append('description', formData.description);
        if (file) {
          body.append('file', file);
        }
      } else {
        endpoint = `${API}/request/service`;
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({
          service_type: serviceType,
          contact_name: formData.name,
          contact_email: formData.email,
          description: formData.description,
          files_attached: false
        });
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: body,
      });

      if (response.ok) {
        // Track service request in Google Analytics
        trackServiceRequest(serviceType);
        
        alert('Service request submitted successfully! We will contact you within 24 hours.');
      } else {
        alert('Error submitting service request');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting service request');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Clean Ambiq Style */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            {/* Logo with minimum width */}
            <div className="flex items-center flex-shrink-0">
              <img 
                src="/logo-nav.png" 
                alt="Create 3D Works Logo" 
                className="h-10 sm:h-12 w-auto object-contain"
                style={{ minWidth: '120px' }}
              />
            </div>

            {/* Spacer matching menu item spacing */}
            <div className="w-8 flex-shrink-0 hidden lg:block"></div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8 items-center">
              <button onClick={() => navigateToPage("services")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Services</button>
              <button onClick={() => navigateToPage("products")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Shop</button>
              <button onClick={() => navigateToPage("marketplace")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Designs</button>
              <button onClick={() => navigateToPage("about")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">About</button>
              <button onClick={() => navigateToPage("contact")} className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors">Contact</button>
            </nav>

            {/* Right-aligned Cart and Auth buttons for Desktop */}
            <div className="hidden lg:flex items-center space-x-4 ml-auto">
              {/* Shopping Cart */}
              <button 
                onClick={() => navigateToPage("cart")}
                className="relative flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#0007b5] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
                <span className="hidden sm:inline">Cart</span>
              </button>
              
              {/* Firebase Authentication */}
              {loading ? (
                <div className="flex items-center space-x-2 font-medium text-slate-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0007b5]"></div>
                  <span>Loading...</span>
                </div>
              ) : isAuthenticated ? (
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => navigateToPage("profile")}
                    className="flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span>{user?.name || 'Profile'}</span>
                  </button>
                  <button 
                    onClick={logout}
                    className="font-medium text-slate-600 hover:text-[#0007b5] transition-colors text-sm"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => setShowLoginModal(true)}
                  className="flex items-center space-x-2 bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Sign In</span>
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden items-center gap-3 ml-auto">
              {/* Cart for mobile */}
              <button 
                onClick={() => navigateToPage("cart")}
                className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ShoppingCart className="w-6 h-6 text-slate-600" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#0007b5] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {cart.length}
                  </span>
                )}
              </button>

              {/* Hamburger menu button */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-slate-600" />
                ) : (
                  <div className="space-y-1.5">
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                    <div className="w-6 h-0.5 bg-slate-600"></div>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu Panel */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-slate-100 pt-4">
              <nav className="flex flex-col space-y-4">
                <button onClick={() => {navigateToPage("services"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Services</button>
                <button onClick={() => {navigateToPage("products"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Shop</button>
                <button onClick={() => {navigateToPage("marketplace"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Designs</button>
                <button onClick={() => {navigateToPage("about"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">About</button>
                <button onClick={() => {navigateToPage("contact"); setMobileMenuOpen(false);}} className="text-left font-medium text-slate-600 hover:text-[#0007b5] transition-colors py-2">Contact</button>

                <div className="border-t border-slate-200 my-2"></div>

                {isAuthenticated ? (
                  <>
                    <button onClick={() => {navigateToPage("profile"); setMobileMenuOpen(false);}} className="text-left flex items-center space-x-2 py-2 text-slate-600 hover:text-[#0007b5] transition-colors">
                      <User className="w-4 h-4" />
                      <span>{user?.name || 'Profile'}</span>
                    </button>
                    <button onClick={() => {logout(); setMobileMenuOpen(false);}} className="text-left py-2 text-slate-600 hover:text-[#0007b5] transition-colors">Sign Out</button>
                  </>
                ) : (
                  <button 
                    onClick={() => {setShowLoginModal(true); setMobileMenuOpen(false);}}
                    className="bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 w-full"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Sign In</span>
                  </button>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Global Payment Status Messages */}
      {paymentStatus === 'success' && (
        <div className="bg-brand-green-50 border-l-4 border-brand-green-500 p-4 mx-4 mt-4 rounded-r-lg">
          <div className="flex items-center">
            <Check className="w-6 h-6 text-brand-green-500 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-brand-green-800">Payment Successful! 🎉</h3>
              <p className="text-brand-green-700">Your payment has been processed successfully. Your order is now confirmed and will begin production soon.</p>
              <div className="mt-3 space-x-4">
                <button
                  onClick={() => navigateToPage("profile")}
                  className="bg-brand-green-600 text-white px-4 py-2 rounded-lg hover:bg-brand-green-700 transition-colors text-sm font-medium"
                >
                  View Order Status
                </button>
                <button
                  onClick={() => setPaymentStatus(null)}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {paymentStatus === 'checking' && (
        <div className="bg-brand-blue-50 border-l-4 border-brand-blue-500 p-4 mx-4 mt-4 rounded-r-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-blue-600 mr-3"></div>
            <div>
              <h3 className="text-lg font-semibold text-brand-blue-800">Verifying Payment...</h3>
              <p className="text-brand-blue-700">Please wait while we confirm your payment with Stripe.</p>
            </div>
          </div>
        </div>
      )}

      {paymentError && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 mt-4 rounded-r-lg">
          <div className="flex items-center">
            <span className="text-red-500 text-xl mr-3">❌</span>
            <div>
              <h3 className="text-lg font-semibold text-red-800">Payment Issue</h3>
              <p className="text-red-700">{paymentError}</p>
              <button
                onClick={() => setPaymentError(null)}
                className="mt-2 bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors text-sm"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section - Video Background */}
      <section className="relative min-h-screen overflow-hidden bg-slate-900">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectFit: 'cover' }}
          >
            <source src="/C3D_Banner_Video_2.mp4" type="video/mp4" />
          </video>
          
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-slate-900/75"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-32">
          <div className="text-center">
            {/* Clean Typography - Ambiq Style */}
            <div className="mb-8">
              <h1 className="font-black leading-none">
                <span className="block text-5xl md:text-7xl lg:text-8xl text-white">3D Printing</span>
                <span className="block text-4xl md:text-6xl lg:text-7xl mt-2 text-blue-400">Tailored to Your Needs</span>
              </h1>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 mt-6">
                <span className="text-white">Let us</span>{' '}
                <span className="text-blue-400">design your parts</span>{' '}
                <span className="text-white">or</span>{' '}
                <span className="text-blue-400">print yours.</span>
              </h2>
            </div>

            {/* Clean Description */}
            <p className="text-xl md:text-2xl text-slate-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
              Use our <span className="text-white font-bold">instant quote tool</span> to <span className="text-white font-bold">print your own parts</span> to your specs. Don't have a 3D model? <span className="text-white font-bold">Let us design your part</span> from scratch, from a hand sketch, or convert a 2D drawing.
            </p>

            {/* Clean CTA Buttons - Ambiq Style */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button 
                size="lg" 
                className="bg-[#0007b5] hover:bg-[#000690] text-white px-12 py-4 text-lg font-semibold rounded-lg shadow-xl transition-all duration-300"
                onClick={scrollToQuoteSection}
              >
                Get Instant Quote
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0007b5] px-12 py-4 text-lg font-semibold rounded-lg transition-all duration-300"
                onClick={() => navigateToPage("marketplace")}
              >
                Explore CAD Library
                <FileText className="ml-2 w-5 h-5" />
              </Button>
              
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0007b5] px-12 py-4 text-lg font-semibold rounded-lg transition-all duration-300"
                onClick={() => navigateToPage("products")}
              >
                Explore Products
                <Package className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Trust Indicators - Clean Style */}
            <div className="mt-16 pt-8 border-t border-slate-700">
              <p className="text-slate-400 text-xs font-medium tracking-widest uppercase mb-6">Trusted by</p>
              <div className="flex items-center justify-center space-x-8 text-slate-500 text-base font-medium">
                <span>GOVERNMENT</span>
                <span>•</span>
                <span>UNIVERSITIES</span>
                <span>•</span>
                <span>COMMERCIAL</span>
              </div>
            </div>
          </div>
        </div>

        {/* Clean scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-5 h-5 text-slate-400 rotate-90" />
        </div>
      </section>

      {/* Services Overview - Ambiq-Inspired Fluid Design */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Number - Ambiq Style */}
          <div className="flex items-center mb-16">
            <div className="text-8xl font-black text-slate-100 mr-8">01</div>
            <div>
              <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">ENGINEERING EXPERTISE</div>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900">
                Flexible Solutions
              </h3>
            </div>
          </div>
          
          {/* Services Grid - Two Row Layout */}
          <div className="space-y-8 mb-20">
            {/* First Row - 2 Larger Cards (Primary Services) */}
            <div className="grid md:grid-cols-2 gap-8">
            <div 
              className="group cursor-pointer h-full"
              onClick={() => {
                console.log('3D File Printing clicked'); // Debug log
                setActiveTab("upload");
                
                setTimeout(() => {
                  // Target the quote interface section where the tools are
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  console.log('Quote section found:', quoteSection); // Debug log
                  
                  if (quoteSection) {
                    // Calculate absolute position from top of document
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100; // 100px offset for better positioning
                    
                    console.log('Current scroll position:', window.pageYOffset);
                    console.log('Element rect.top:', rect.top);
                    console.log('Calculated absolute scroll position:', absoluteTop);
                    
                    // Use window.scrollTo for more reliable scrolling
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Upload className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">3D File Printing</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Professional analysis of your CAD files with material recommendations tailored to your specific use case and requirements.
                </p>
              </div>
            </div>

            <div 
              className="group cursor-pointer h-full"
              onClick={() => {
                setActiveTab("design");
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Zap className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">Design Services</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Complete design-to-production services with engineering expertise in functional, cosmetic, and load-bearing applications.
                </p>
              </div>
            </div>
            </div>

            {/* Second Row - 3 Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div 
              className="group cursor-pointer h-full"
              onClick={() => {
                console.log('2D to 3D Conversion clicked'); // Debug log
                setActiveTab("convert");
                
                setTimeout(() => {
                  // Target the quote interface section where the tools are
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  console.log('Quote section found:', quoteSection); // Debug log
                  
                  if (quoteSection) {
                    // Calculate absolute position from top of document
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100; // 100px offset for better positioning
                    
                    console.log('Current scroll position:', window.pageYOffset);
                    console.log('Element rect.top:', rect.top);
                    console.log('Calculated absolute scroll position:', absoluteTop);
                    
                    // Use window.scrollTo for more reliable scrolling
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <FileText className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">2D to 3D Conversion</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Expert engineering services to transform technical drawings into optimized 3D models ready for production.
                </p>
              </div>
            </div>
            <div 
              className="group cursor-pointer h-full"
              onClick={() => {
                setActiveTab("optimize");
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Settings className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">Product Optimization</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Engineering analysis and optimization for maximum performance, durability, and manufacturing efficiency.
                </p>
              </div>
            </div>

            <div 
              className="group cursor-pointer h-full"
              onClick={() => {
                setActiveTab("renders");
                setTimeout(() => {
                  const quoteSection = document.querySelector('[data-section="quote-interface"]');
                  if (quoteSection) {
                    const rect = quoteSection.getBoundingClientRect();
                    const absoluteTop = rect.top + window.pageYOffset - 100;
                    window.scrollTo({
                      top: absoluteTop,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border border-slate-100 h-full flex flex-col">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Eye className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-4">Product Renders</h4>
                <p className="text-slate-600 leading-relaxed flex-1">
                  Realistic renders of 3D printed products including layer visualization to see how your finished component will look.
                </p>
              </div>
            </div>
            </div>
          </div>

          {/* Feature Highlights - Ambiq Style */}
          <div className="text-center">
            <p className="text-slate-600 text-lg max-w-4xl mx-auto leading-relaxed">
              Engineering-driven approach with experience across prototype, cosmetic, functional, load-bearing, 
              and wrapped components. Flexible solutions for every application.
            </p>
          </div>
        </div>
      </section>

      {/* Technology Section - Ambiq-Inspired */}
      <section id="about" className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Number - Ambiq Style */}
          <div className="flex items-center mb-16">
            <div className="text-8xl font-black text-slate-200 mr-8">02</div>
            <div>
              <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">ENGINEERING EXPERTISE</div>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900">
                Proven Experience
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            {/* Left Content */}
            <div className="space-y-8">
              <div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Engineering-Driven Solutions</h4>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Created by a seasoned "skunkworks team" with over 8 combined years of hands-on 3D printing experience and designing end-use parts specifically for 3D printing. We understand the technical challenges and provide solutions that work.
                </p>
              </div>
              
              <div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Complete Application Range</h4>
                <p className="text-lg text-slate-600 leading-relaxed">
                  From prototype testing to production-grade components. Experience with cosmetic parts, functional assemblies, 
                  load-bearing structures, smoothed surfaces, painted finishes, and wrapped components.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-black text-slate-900 mb-1">Custom</div>
                  <div className="text-sm text-slate-600 font-medium">Design Parts</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-slate-900 mb-1">3,000+ lb</div>
                  <div className="text-sm text-slate-600 font-medium">Filament Printed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-slate-900 mb-1">Every Use</div>
                  <div className="text-sm text-slate-600 font-medium">Case Covered</div>
                </div>
              </div>
            </div>

            {/* Right Content - Clean Image */}
            <div className="relative">
              <img 
                src="/home_printed_components_2.png"
                alt="Precision Mechanical Components"
                className="rounded-2xl shadow-2xl w-full h-auto object-contain"
              />
            </div>
          </div>

        </div>
      </section>

      {/* Main Service Interface - Clean Style */}
      <section className="py-24 bg-white relative overflow-hidden" data-section="quote-interface">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          <div id="instant-quote" className="text-center mb-16">
            <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">GET STARTED</div>
            <h3 className="text-4xl md:text-5xl font-black text-slate-900">
              Start Your Project
            </h3>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-12 bg-slate-50 rounded-2xl p-1 border-0">
              <TabsTrigger value="upload" className="flex items-center space-x-2 rounded-xl font-medium">
                <Upload className="w-4 h-4" />
                <span>Upload & Quote</span>
              </TabsTrigger>
              <TabsTrigger value="convert" className="flex items-center space-x-2 rounded-xl font-medium">
                <FileText className="w-4 h-4" />
                <span>2D to 3D</span>
              </TabsTrigger>
              <TabsTrigger value="design" className="flex items-center space-x-2 rounded-xl font-medium">
                <Zap className="w-4 h-4" />
                <span>Design</span>
              </TabsTrigger>
              <TabsTrigger value="optimize" className="flex items-center space-x-2 rounded-xl font-medium">
                <Settings className="w-4 h-4" />
                <span>Optimize</span>
              </TabsTrigger>
              <TabsTrigger value="renders" className="flex items-center space-x-2 rounded-xl font-medium">
                <Eye className="w-4 h-4" />
                <span>Renders</span>
              </TabsTrigger>
            </TabsList>

            {/* 3D File Upload Tab */}
            <TabsContent value="upload">
              <Card className="border-0 shadow-lg rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Upload className="w-5 h-5 text-[#0007b5]" />
                    <span>Upload 3D File for Instant Quote</span>
                  </CardTitle>
                  <CardDescription>
                    Upload your 3D CAD file and customize your printing preferences to get an instant quote.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleQuoteSubmit} className="space-y-6">
                    {/* File Upload Section - Full Width */}
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="file-upload">3D File Upload</Label>
                        {formData.file ? (
                          // Show newly selected file
                          <div className="mt-2">
                            <div className="flex items-center justify-between p-4 bg-blue-50 border-2 border-blue-400 rounded-lg">
                              <div className="flex items-center space-x-3 flex-1">
                                <FileText className="h-6 w-6 text-[#0007b5] flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-blue-900 truncate">
                                    {formData.file.name}
                                  </p>
                                  <p className="text-xs text-blue-700 mt-0.5">
                                    {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => document.getElementById('file-upload').click()}
                                className="ml-3 px-4 py-2 bg-white border border-[#0007b5] text-blue-700 rounded-md hover:bg-blue-50 text-sm font-medium flex-shrink-0"
                              >
                                Change File
                              </button>
                            </div>
                            <Input
                              id="file-upload"
                              type="file"
                              accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                              onChange={handleFileUpload}
                              className="hidden"
                            />
                          </div>
                        ) : formData.existing_quote_id && formData.preserved_filename ? (
                          // Show preserved file with option to change
                          <div className="mt-2">
                            <div className="flex items-center justify-between p-4 bg-green-50 border-2 border-green-400 rounded-lg">
                              <div className="flex items-center space-x-3 flex-1">
                                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-green-900 truncate">
                                    {formData.preserved_filename}
                                  </p>
                                  <p className="text-xs text-green-700 mt-0.5">
                                    File preserved from original quote
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => document.getElementById('file-upload').click()}
                                className="ml-3 px-4 py-2 bg-white border border-green-600 text-green-700 rounded-md hover:bg-green-50 text-sm font-medium flex-shrink-0"
                              >
                                Change File
                              </button>
                            </div>
                            <Input
                              id="file-upload"
                              type="file"
                              accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                              onChange={handleFileUpload}
                              className="hidden"
                            />
                            <p className="text-xs text-slate-500 mt-2">
                              Click "Change File" to upload a different file, or keep this file and continue editing other parameters.
                            </p>
                          </div>
                        ) : (
                          // Show normal file upload
                          <>
                            <Input
                              id="file-upload"
                              type="file"
                              accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                              onChange={handleFileUpload}
                              required={!formData.existing_quote_id}
                              className="mt-2"
                            />
                            <p className="text-sm text-slate-500 mt-1">
                              Supported: STL, OBJ, 3MF, PLY, AMF, STEP
                            </p>
                          </>
                        )}
                        
                        {/* Units Selection */}
                        <div className="mt-4">
                          <Label htmlFor="units">CAD File Units</Label>
                          <Select value={formData.units} onValueChange={(value) => setFormData({...formData, units: value})} required>
                            <SelectTrigger className="mt-2">
                              <SelectValue placeholder="Choose units" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="mm">Millimeters (mm)</SelectItem>
                              <SelectItem value="cm">Centimeters (cm)</SelectItem>
                              <SelectItem value="m">Meters (m)</SelectItem>
                              <SelectItem value="in">Inches (in)</SelectItem>
                              <SelectItem value="ft">Feet (ft)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-sm text-slate-500 mt-1">Select the units used in your CAD file</p>
                        </div>
                        
                        {/* 3D Model Preview */}
                        {formData.file && (
                          <div className="mt-6">
                            <ThreeDPreview 
                              file={formData.file}
                              material={formData.material}
                              strength={formData.strength}
                              quality={formData.quality}
                              units={formData.units}
                              onAnalysis={(analysisData) => {
                                console.log('3D Analysis:', analysisData);
                                setFormData(prev => ({
                                  ...prev,
                                  analysisData: analysisData
                                }));
                              }}
                              className="w-full"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Part Section with Subheading */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Part</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="material">Material</Label>
                            <Select value={formData.material} onValueChange={(value) => setFormData({...formData, material: value})} required>
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Choose material" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="PLA">PLA - Standard</SelectItem>
                                <SelectItem value="PLA+">PLA+ - Reinforced</SelectItem>
                                <SelectItem value="Silk PLA">Silk PLA - Glossy finish</SelectItem>
                                <SelectItem value="Wood-fill PLA">Wood-fill PLA - Reinforced, Wood-like finish</SelectItem>
                                <SelectItem value="Metal-fill PLA">Metal-fill PLA - Reinforced, Metal-like finish</SelectItem>
                                <SelectItem value="CF PLA">CF PLA - Carbon Fiber reinforced</SelectItem>
                                <SelectItem value="HT PLA">HT PLA - High Temp PLA</SelectItem>
                                <SelectItem value="PETG">PETG</SelectItem>
                                <SelectItem value="CPE+">CPE+</SelectItem>
                                <SelectItem value="ABS">ABS</SelectItem>
                                <SelectItem value="ABS+">ABS+</SelectItem>
                                <SelectItem value="ASA">ASA</SelectItem>
                                <SelectItem value="TPU">TPU</SelectItem>
                                <SelectItem value="TPU 85A">TPU 85A</SelectItem>
                                <SelectItem value="TPU 64D">TPU 64D (semi-rigid)</SelectItem>
                                <SelectItem value="PC">PC - coming soon!</SelectItem>
                                <SelectItem value="PC-CF">PC-CF - coming soon!</SelectItem>
                                <SelectItem value="PA">PA - coming soon!</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="quality">Print Quality</Label>
                            <Select value={formData.quality} onValueChange={(value) => setFormData({...formData, quality: value})} required>
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Choose quality" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Concept">Concept - Quick Validation</SelectItem>
                                <SelectItem value="Prototype">Prototype - Functional Testing</SelectItem>
                                <SelectItem value="Test/Demo">Test/Demo - Demonstration Ready</SelectItem>
                                <SelectItem value="End Use">End Use - Production Quality</SelectItem>
                                <SelectItem value="Display">Display - Premium Finish</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="strength">Strength Requirements</Label>
                            <Select value={formData.strength} onValueChange={(value) => setFormData({...formData, strength: value})} required>
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Choose strength" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Cosmetic Only">Cosmetic Only - Display Model</SelectItem>
                                <SelectItem value="Reinforced">Reinforced - Light Use</SelectItem>
                                <SelectItem value="Functional">Functional - Heavy Duty</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="color">Color</Label>
                            <Select 
                              value={formData.color} 
                              onValueChange={(value) => setFormData({...formData, color: value})} 
                              required
                            >
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Choose color" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Any">Any</SelectItem>
                                <SelectItem value="Black">Black</SelectItem>
                                <SelectItem value="White">White</SelectItem>
                                <SelectItem value="Grey">Grey</SelectItem>
                                <SelectItem value="Red">Red</SelectItem>
                                <SelectItem value="Blue">Blue</SelectItem>
                                <SelectItem value="Other">Other (specify below)</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            {formData.color === 'Other' && (
                              <div className="mt-3">
                                <Label htmlFor="customColor">Custom Color</Label>
                                <Input
                                  id="customColor"
                                  type="text"
                                  placeholder="e.g., Green, Yellow, Purple"
                                  value={formData.customColor}
                                  onChange={(e) => setFormData({...formData, customColor: e.target.value})}
                                  required
                                  className="mt-2"
                                />
                              </div>
                            )}
                          </div>

                          <div>
                            <Label htmlFor="smoothing">Surface Smoothing</Label>
                            <Select value={formData.smoothing} onValueChange={(value) => setFormData({...formData, smoothing: value})} required>
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Choose smoothing" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="None - As Printed">None - As Printed</SelectItem>
                                <SelectItem value="Light Sanding">Light Sanding</SelectItem>
                                <SelectItem value="Smooth Finish">Smooth Finish</SelectItem>
                                <SelectItem value="Premium Finish">Premium Finish</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="contact_email">Contact Email</Label>
                            <Input
                              id="contact_email"
                              type="email"
                              placeholder="your@email.com"
                              value={formData.contact_email}
                              onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                              required
                              className="mt-2"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Shipping Section with Subheading - Side by Side */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Shipping</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="country_code">Shipping Destination</Label>
                          <Select value={formData.country_code} onValueChange={(value) => setFormData({...formData, country_code: value})} required>
                            <SelectTrigger className="mt-2">
                              <SelectValue placeholder="Choose destination" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="CA">Canada (Domestic)</SelectItem>
                              <SelectItem value="US">United States (+20%)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-sm text-slate-500 mt-1">Shipping from Canada</p>
                        </div>
                        
                        <div>
                          <Label htmlFor="postal_code">Postal/Zip Code (Optional)</Label>
                          <Input
                            id="postal_code"
                            type="text"
                            placeholder={formData.country_code === "CA" ? "A1A 1A1" : "12345"}
                            value={formData.postal_code}
                            onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                            className="mt-2"
                            maxLength={10}
                          />
                          <p className="text-sm text-slate-500 mt-1">For accurate shipping estimate</p>
                        </div>
                      </div>
                    </div>

                    {/* Full-width Additional Notes */}
                    <div className="mb-6">
                      <Label htmlFor="notes">Additional Notes (Optional)</Label>
                      <Textarea
                        id="notes"
                        placeholder="Any special requirements or notes..."
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="mt-2"
                        rows={3}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loadingQuote}
                      className="w-full bg-[#0007b5] hover:bg-[#000690] text-white py-3 text-lg font-semibold rounded-lg transition-colors"
                    >
                      {loadingQuote ? "Generating Quote..." : "Get Instant Quote"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Quote Results Tab */}
            <TabsContent value="quote">
              {quoteData && (
                <Card className="border-0 shadow-lg rounded-2xl">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center space-x-2">
                          <Check className="w-5 h-5 text-green-600" />
                          <span>Your Quote is Ready!</span>
                        </CardTitle>
                        <CardDescription className="mt-2">
                          Quote ID: <Badge variant="outline">{quoteData.quote_id}</Badge>
                        </CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          // Preserve the quote_id and filename so the file doesn't need to be re-uploaded
                          setFormData(prev => ({
                            ...prev,
                            existing_quote_id: quoteData.quote_id,
                            preserved_filename: quoteData.material_analysis?.filename || 'original file'
                          }));
                          setActiveTab("upload");
                        }}
                        className="flex items-center space-x-2"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Edit Quote</span>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-3">Price Breakdown</h4>
                        <div className="space-y-2 text-sm">
                          {/* TODO: HIDE BEFORE DEPLOYMENT - Material weight shown for testing only */}
                          {quoteData.price_breakdown.required_material_weight && (
                            <div className="flex justify-between text-slate-600">
                              <span>Required Material Weight:</span>
                              <span>{quoteData.price_breakdown.required_material_weight.toFixed(2)}g</span>
                            </div>
                          )}
                          {/* TODO: HIDE BEFORE DEPLOYMENT - Infill % shown for testing only */}
                          {quoteData.price_breakdown.infill_percentage && (
                            <div className="flex justify-between text-slate-600">
                              <span>Infill Percentage:</span>
                              <span>{quoteData.price_breakdown.infill_percentage.toFixed(0)}%</span>
                            </div>
                          )}
                          {/* TODO: HIDE BEFORE DEPLOYMENT - Adjustment factors shown for testing only */}
                          {quoteData.price_breakdown.weight_adjustment_factor && (
                            <div className="flex justify-between text-slate-600">
                              <span>Weight Adjustment Factor:</span>
                              <span>{quoteData.price_breakdown.weight_adjustment_factor.toFixed(4)}</span>
                            </div>
                          )}
                          {quoteData.price_breakdown.actual_print_weight && (
                            <div className="flex justify-between text-slate-600">
                              <span>Actual Print Weight:</span>
                              <span>{quoteData.price_breakdown.actual_print_weight.toFixed(2)}g</span>
                            </div>
                          )}
                          {quoteData.price_breakdown.material_cost_per_gram && (
                            <div className="flex justify-between text-slate-600">
                              <span>Material Cost per Gram:</span>
                              <span>${quoteData.price_breakdown.material_cost_per_gram.toFixed(4)}/g</span>
                            </div>
                          )}
                          {quoteData.price_breakdown.quality_adjustment_factor && (
                            <div className="flex justify-between text-slate-600">
                              <span>Quality Adjustment Factor:</span>
                              <span>{quoteData.price_breakdown.quality_adjustment_factor.toFixed(2)}x</span>
                            </div>
                          )}
                          {quoteData.price_breakdown.cost_adjustment_factor && (
                            <div className="flex justify-between text-slate-600">
                              <span>Cost Adjustment Factor:</span>
                              <span>{quoteData.price_breakdown.cost_adjustment_factor.toFixed(4)}x</span>
                            </div>
                          )}
                          {quoteData.price_breakdown.complexity_adjustment_factor && (
                            <div className="flex justify-between text-slate-600">
                              <span>Complexity Adjustment Factor:</span>
                              <span>{quoteData.price_breakdown.complexity_adjustment_factor.toFixed(4)}x</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span>Print Cost:</span>
                            <span>{formatPrice(quoteData.price_breakdown.print_cost)}</span>
                          </div>
                          {quoteData.price_breakdown.smoothing_cost > 0 && (
                            <div className="flex justify-between">
                              <span>Smoothing Cost:</span>
                              <span>{formatPrice(quoteData.price_breakdown.smoothing_cost)}</span>
                            </div>
                          )}
                          {/* TODO: HIDE BEFORE DEPLOYMENT - Complexity shown for testing only */}
                          <div className="flex justify-between">
                            <span>Complexity Factor:</span>
                            <span>{(quoteData.material_analysis.complexity_score * 100).toFixed(0)}%</span>
                          </div>
                          {/* TODO: HIDE BEFORE DEPLOYMENT - Volume & surface area shown for testing only */}
                          {quoteData.material_analysis.volume && (
                            <div className="flex justify-between text-slate-600">
                              <span>Volume:</span>
                              <span>{quoteData.material_analysis.volume.toFixed(2)} cm³</span>
                            </div>
                          )}
                          {quoteData.material_analysis.surface_area && (
                            <div className="flex justify-between text-slate-600">
                              <span>Surface Area:</span>
                              <span>{quoteData.material_analysis.surface_area.toFixed(2)} cm²</span>
                            </div>
                          )}
                          {quoteData.material_analysis.volume_area_ratio && (
                            <div className="flex justify-between text-slate-600">
                              <span>Volume/Area Ratio:</span>
                              <span>{quoteData.material_analysis.volume_area_ratio.toFixed(4)}</span>
                            </div>
                          )}
                          {quoteData.price_breakdown.smoothing_cost > 0 && (
                            <div className="flex justify-between">
                              <span>Smoothing Cost:</span>
                              <span>{formatPrice(quoteData.price_breakdown.smoothing_cost)}</span>
                            </div>
                          )}
                          {quoteData.price_breakdown.shipping_cost && (
                            <>
                              <div className="flex justify-between">
                                <span>Shipping ({quoteData.price_breakdown.shipping_details?.package_size || 'Standard'}):</span>
                                <span>{formatPrice(quoteData.price_breakdown.shipping_cost)}</span>
                              </div>
                              {quoteData.price_breakdown.shipping_details && (
                                <div className="text-xs text-slate-500 ml-4">
                                  {quoteData.price_breakdown.shipping_details.shipping_zone} • {quoteData.price_breakdown.shipping_details.estimated_delivery_days}
                                </div>
                              )}
                            </>
                          )}
                          <Separator />
                          <div className="flex justify-between font-semibold text-lg">
                            <span>Total:</span>
                            <span className="text-[#0007b5]">{formatPrice(quoteData.estimated_price)}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-3">Timeline</h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-slate-600">Production Time</p>
                            <p className="text-lg font-bold text-slate-700">{quoteData.estimated_time}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-600">Delivery Estimate</p>
                            <p className="text-2xl font-bold text-slate-700">{quoteData.delivery_weeks} weeks</p>
                            <p className="text-sm text-slate-500">From order confirmation</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="pt-4">
                      {/* Add to Cart Button */}
                      <div className="space-y-3">
                        <Button 
                          onClick={handleCreateOrder}
                          disabled={loadingOrder}
                          className="w-full bg-[#0007b5] hover:bg-[#000690]"
                        >
                          {loadingOrder ? "Adding to Cart..." : "🛒 Add to Cart & Checkout"}
                        </Button>
                        
                        {!isAuthenticated && (
                          <p className="text-center text-sm text-slate-500">
                            🔒 Sign in required to add to cart
                          </p>
                        )}
                        
                        <p className="text-center text-xs text-slate-400">
                          Your quote will be saved and added to your cart. Shipping calculated at checkout.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Other Service Tabs */}
            <TabsContent value="convert">
              <ServiceRequestForm 
                title="2D to 3D Conversion Service"
                description="Upload your 2D drawings or blueprints and we'll convert them to printable 3D models."
                serviceType="2D to 3D Conversion"
                fileRequired={true}
                onSubmit={handleServiceRequest}
              />
            </TabsContent>

            <TabsContent value="design">
              <ServiceRequestForm 
                title="Custom Design Service"
                description="Describe your idea and our designers will create a custom 3D model for you."
                serviceType="Design Service"
                fileRequired={false}
                onSubmit={handleServiceRequest}
              />
            </TabsContent>

            <TabsContent value="optimize">
              <ServiceRequestForm 
                title="Product Optimization Service"
                description="Send us your 3D model and we'll optimize it for better printability and performance."
                serviceType="Product Optimization"
                fileRequired={true}
                onSubmit={handleServiceRequest}
              />
            </TabsContent>

            <TabsContent value="renders">
              <ServiceRequestForm 
                title="Product Renders Service"
                description="Get realistic renders of your 3D printed products. We'll create high-quality visualizations including layer views to show exactly how your finished component will look."
                serviceType="Product Renders"
                fileRequired={true}
                onSubmit={handleServiceRequest}
              />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Section 03 - Build Your Own Products */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Number - Ambiq Style */}
          <div className="flex items-center mb-16">
            <div className="text-8xl font-black text-slate-100 mr-8">03</div>
            <div>
              <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">EXPERT SUPPORT</div>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900">
                Build Your Own Products
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Professional Equipment & Support</h4>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Ready to bring 3D printing in-house? We offer professional-grade printers and filaments 
                  with comprehensive support to get you started. Every purchase includes 1 month of free 
                  expert consulting to ensure your success.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Free 1-Month Support:</strong> Direct access to our engineering team for troubleshooting and optimization</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Print Quality Consulting:</strong> Help with failing prints, layer adhesion, and surface finish issues</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Material Guidance:</strong> Recommendations for your specific applications and use cases</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#0007b5] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700"><strong>Setup & Calibration:</strong> Printer setup guidance and calibration support</p>
                  </div>
                </div>
              </div>
              
              <Button 
                className="bg-[#0007b5] hover:bg-[#000690] text-white px-12 py-4 text-lg font-semibold rounded-lg shadow-xl transition-all duration-300"
                onClick={() => navigateToPage("products")}
              >
                Shop Now
                <Package className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Right Content - Visual */}
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_threedworks/artifacts/ffoqhphb_Build%20your%20own%20picture%203.png"
                alt="Professional 3D Printing Setup"
                className="rounded-2xl shadow-2xl w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Section 04 - CAD Library / Marketplace */}
      <section className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Section Number - Ambiq Style */}
          <div className="flex items-center mb-16">
            <div className="text-8xl font-black text-slate-100 mr-8">04</div>
            <div>
              <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">DESIGNER MARKETPLACE</div>
              <h3 className="text-4xl md:text-5xl font-black text-slate-900">
                Explore CAD Library
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content - Features Grid */}
            <div className="space-y-6">
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                Access our curated library of professional 3D models designed specifically for 3D printing. 
                Download files for instant printing or request custom prints directly from designers.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">Print-Ready Files</h4>
                  <p className="text-slate-600 text-sm">
                    Download optimized CAD files ready for immediate 3D printing with any printer.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">Expert Designers</h4>
                  <p className="text-slate-600 text-sm">
                    Models created by experienced engineers and designers who understand 3D printing.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Package className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">Print on Demand</h4>
                  <p className="text-slate-600 text-sm">
                    Order prints directly from the marketplace with material and finish options.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <Eye className="w-6 h-6 text-[#0007b5]" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">3D Preview</h4>
                  <p className="text-slate-600 text-sm">
                    Interactive 3D preview of every model before download or purchase.
                  </p>
                </div>
              </div>

              <div className="pt-6">
                <Button 
                  className="bg-[#0007b5] hover:bg-[#000690] text-white px-12 py-4 text-lg font-semibold rounded-lg shadow-xl transition-all duration-300"
                  onClick={() => navigateToPage("marketplace")}
                >
                  Browse CAD Library
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Right Content - Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-slate-100 rounded-2xl p-8 shadow-xl">
                <div className="space-y-4">
                  {/* Mock design card */}
                  <div className="bg-white rounded-xl p-6 shadow-md border border-slate-200">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h5 className="font-bold text-slate-900 mb-1">Professional CAD Models</h5>
                        <p className="text-sm text-slate-500">Licensed for commercial use</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-[#0007b5]" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Multiple Formats</span>
                      <span className="font-bold text-[#0007b5]">STL • 3MF • STEP</span>
                    </div>
                  </div>

                  {/* Mock stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl p-4 text-center shadow-md">
                      <div className="text-2xl font-black text-[#0007b5] mb-1">100+</div>
                      <div className="text-xs text-slate-600">Models</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center shadow-md">
                      <div className="text-2xl font-black text-[#0007b5] mb-1">50+</div>
                      <div className="text-xs text-slate-600">Categories</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center shadow-md">
                      <div className="text-2xl font-black text-[#0007b5] mb-1">3D</div>
                      <div className="text-xs text-slate-600">Preview</div>
                    </div>
                  </div>

                  {/* Popular categories */}
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <h6 className="font-bold text-slate-900 mb-3 text-sm">Popular Categories</h6>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Automotive</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Aerospace</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Robotics</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Tools</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Brackets</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-blue-100 rounded-2xl opacity-20"></div>
            </div>
          </div>

          {/* Additional Info Banner */}
          <div className="mt-16 bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl font-black text-slate-900 mb-2">Instant Download</div>
                <p className="text-slate-600">Get files immediately after purchase</p>
              </div>
              <div>
                <div className="text-3xl font-black text-slate-900 mb-2">Quality Verified</div>
                <p className="text-slate-600">All models tested and print-ready</p>
              </div>
              <div>
                <div className="text-3xl font-black text-slate-900 mb-2">Designer Support</div>
                <p className="text-slate-600">Direct contact with model creators</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Contact Section - Clean Style */}
      <section id="contact" className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          <div className="text-center mb-16">
            <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-2">GET IN TOUCH</div>
            <h3 className="text-4xl md:text-5xl font-black text-slate-900">
              Contact Us
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                  <Mail className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Email Us</h4>
                <p className="text-slate-600 text-lg">hello@create3dworks.com</p>
                <p className="text-slate-500 text-sm mt-1">Response within 2 hours</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                  <Phone className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Call Us</h4>
                <p className="text-slate-600 text-lg">+1 (555) 123-4567</p>
                <p className="text-slate-500 text-sm mt-1">Mon-Fri 9AM-6PM EST</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-[#0007b5]" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Visit Us</h4>
                <p className="text-slate-600 text-lg">590 Portland St - Unit 116<br />Dartmouth, NS B2W 6B7<br />Canada</p>
                <p className="text-slate-500 text-sm mt-1">Open Mon-Fri 9AM-5PM</p>
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-[#0007b5] to-[#000690] rounded-2xl p-12 text-center">
            <h4 className="text-3xl font-bold text-white mb-4">
              Ready to Start Your Project?
            </h4>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Get an instant quote and see why thousands of engineers trust <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span>.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-[#0007b5] hover:bg-blue-50 px-8 py-4 text-lg rounded-lg"
              onClick={scrollToQuoteSection}
            >
              Get Instant Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Footer - Clean Ambiq Style */}
      <Footer />

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
    </div>
  );
};

// Service Request Form Component
const ServiceRequestForm = ({ title, description, serviceType, fileRequired, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
    file: null
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFormData({ ...formData, file });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (fileRequired && !formData.file) {
      alert('File upload is required for this service');
      return;
    }
    onSubmit(serviceType, formData, formData.file);
    setFormData({ name: "", email: "", description: "", file: null });
  };

  return (
    <Card className="border-0 shadow-lg rounded-2xl">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Your Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="description">Project Description</Label>
            <Textarea
              id="description"
              placeholder="Describe your project requirements..."
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              required
              className="mt-2"
              rows={4}
            />
          </div>
          {(fileRequired || serviceType === "Design Service") && (
            <div>
              <Label htmlFor="file">
                {fileRequired ? "File Upload (Required)" : "File Upload (Optional)"}
              </Label>
              <Input
                id="file"
                type="file"
                accept=".pdf,.dwg,.dxf,.stl,.obj,.3mf,.ply,.amf,.step,.stp,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                required={fileRequired}
                className="mt-2"
              />
              <p className="text-sm text-slate-500 mt-1">
                Accepted: CAD files, 3D models, images, PDFs
              </p>
              {formData.file && (
                <div className="mt-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    ✓ {formData.file.name}
                  </Badge>
                </div>
              )}
            </div>
          )}
          <Button type="submit" className="w-full bg-[#0007b5] hover:bg-[#000690]">
            Submit Request
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Page Components
const PageLayout = ({ title, children, navigateHome }) => (
  <div className="min-h-screen bg-white flex flex-col">
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <Button onClick={navigateHome} variant="ghost" className="flex items-center space-x-2">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Button>
          <button 
            onClick={navigateHome}
            className="absolute left-1/2 transform -translate-x-1/2 flex items-center"
          >
            <img 
              src="/logo-nav.png" 
              alt="Create 3D Works Logo" 
              className="h-10 sm:h-12 w-auto object-contain"
              style={{ minWidth: '120px' }}
            />
          </button>
        </div>
      </div>
    </header>
    <div className="flex-grow">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-800 mb-8">{title}</h1>
        {children}
      </div>
    </div>
    <Footer onNavigate={navigateHome} />
  </div>
);

// About Us Page
const AboutUsPage = ({ navigateHome }) => (
  <PageLayout title={<span>About <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span></span>} navigateHome={navigateHome}>
    <div className="prose prose-lg max-w-none">
      <p className="text-xl text-slate-600 mb-8">
        <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> is a leading provider of professional 3D printing services, serving customers worldwide with precision manufacturing solutions. 
        Founded with a mission to make advanced 3D printing accessible to everyone, we combine cutting-edge technology with expert craftsmanship 
        to deliver exceptional results for every project.
      </p>
      
      <h2 className="text-3xl font-bold text-slate-800 mb-6">Our Mission</h2>
      <p className="text-slate-600 mb-8">
        To democratize access to professional-grade 3D printing technology and empower innovators, engineers, and creators 
        to bring their ideas to life with uncompromising quality and precision.
      </p>
      
      <h2 className="text-3xl font-bold text-slate-800 mb-6">Why Choose Us</h2>
      <ul className="text-slate-600 space-y-4">
        <li>• State-of-the-art 3D printing equipment and technology</li>
        <li>• Expert team with years of experience in additive manufacturing</li>
        <li>• Wide range of materials and finishing options</li>
        <li>• Fast turnaround times without compromising quality</li>
        <li>• Competitive pricing and transparent quoting</li>
        <li>• Dedicated customer support throughout your project</li>
      </ul>
    </div>
  </PageLayout>
);

// FAQ Page
const FAQPage = ({ navigateHome }) => (
  <PageLayout title="Frequently Asked Questions" navigateHome={navigateHome}>
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">What file formats do you accept?</h3>
        <p className="text-slate-600">
          We accept all major 3D file formats including STL, OBJ, 3MF, PLY, AMF, and STEP files. 
          Our system can automatically process and analyze these formats to provide instant quotes.
        </p>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">How accurate are your prints?</h3>
        <p className="text-slate-600">
          Our 3D printers achieve accuracy levels of ±0.1mm for most materials and geometries. 
          We use industrial-grade equipment to ensure consistent, high-quality results.
        </p>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">What materials do you offer?</h3>
        <p className="text-slate-600">
          We offer a wide range of materials including PLA, ABS, PETG, TPU, Wood Fill, Metal Fill, 
          and Carbon Fiber composites. Each material has different properties suitable for various applications.
        </p>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">How long does shipping take?</h3>
        <p className="text-slate-600">
          Standard shipping takes 4-6 weeks from order confirmation. Express options are available 
          for faster delivery. Shipping times may vary based on complexity and current order volume.
        </p>
      </div>
    </div>
  </PageLayout>
);

// Terms Page
const TermsPage = ({ navigateHome }) => (
  <PageLayout title="Terms of Service" navigateHome={navigateHome}>
    <div className="prose prose-lg max-w-none text-slate-600">
      <p className="text-lg mb-8">
        By accessing and using Create 3D Works services, you agree to be bound by these Terms of Service and all applicable laws and regulations.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Service Description</h2>
      <p className="mb-6">
        Create 3D Works provides 3D printing services, design consultation, and related manufacturing services. All services are subject to availability and technical feasibility.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Intellectual Property</h2>
      <p className="mb-6">
        You retain all rights to your designs and files. Create 3D Works will not reproduce, distribute, or use your intellectual property without explicit permission.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Quality and Accuracy</h2>
      <p className="mb-6">
        While we strive for the highest quality, 3D printing involves inherent tolerances and limitations. We guarantee reasonable commercial accuracy within stated tolerances.
      </p>
      
      <h2 className="text-2xl font-bold text-slate-800 mb-4">Payment and Pricing</h2>
      <p className="mb-6">
        Prices are quoted based on current material costs and complexity analysis. Final pricing may be adjusted for unusual complexity or material requirements.
      </p>
    </div>
  </PageLayout>
);

// File Formats Page
const FileFormatsPage = ({ navigateHome }) => (
  <PageLayout title="Supported File Formats" navigateHome={navigateHome}>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">3D Model Formats</h3>
        <ul className="space-y-4">
          <li className="flex items-center space-x-3">
            <Badge className="bg-green-100 text-green-800">STL</Badge>
            <span className="text-slate-600">Standard Tessellation Language - Most common format</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-blue-100 text-blue-800">OBJ</Badge>
            <span className="text-slate-600">Wavefront OBJ - Supports textures and materials</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-purple-100 text-purple-800">3MF</Badge>
            <span className="text-slate-600">3D Manufacturing Format - Microsoft standard</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-orange-100 text-orange-800">PLY</Badge>
            <span className="text-slate-600">Stanford Triangle Format - Polygon file format</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-red-100 text-red-800">AMF</Badge>
            <span className="text-slate-600">Additive Manufacturing File Format</span>
          </li>
          <li className="flex items-center space-x-3">
            <Badge className="bg-indigo-100 text-indigo-800">STEP</Badge>
            <span className="text-slate-600">Standard for Exchange of Product Data - CAD format</span>
          </li>
        </ul>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">File Requirements</h3>
        <ul className="space-y-3 text-slate-600">
          <li>• Maximum file size: 100MB</li>
          <li>• Minimum feature size: 0.5mm</li>
          <li>• Recommended layer height: 0.1-0.3mm</li>
          <li>• Models should be watertight (closed meshes)</li>
          <li>• Complex geometries may require support structures</li>
          <li>• High-resolution models preferred for detailed parts</li>
        </ul>
      </div>
    </div>
  </PageLayout>
);

// Material Guide Page
const MaterialGuidePage = ({ navigateHome }) => (
  <PageLayout title="Material Selection Guide" navigateHome={navigateHome}>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-green-700">PLA</CardTitle>
          <CardDescription>Biodegradable thermoplastic</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• Easy to print, low warping</li>
            <li>• Good for prototypes and display models</li>
            <li>• Print temperature: 190-220°C</li>
            <li>• Biodegradable and recyclable</li>
          </ul>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-blue-700">ABS</CardTitle>
          <CardDescription>Durable engineering plastic</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• Strong and impact resistant</li>
            <li>• Good for functional parts</li>
            <li>• Print temperature: 220-250°C</li>
            <li>• Requires heated bed</li>
          </ul>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-purple-700">PETG</CardTitle>
          <CardDescription>Chemical resistant plastic</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• Crystal clear transparency</li>
            <li>• Chemical and impact resistant</li>
            <li>• Print temperature: 220-250°C</li>
            <li>• Food-safe applications</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  </PageLayout>
);

// Quality Standards Page
const QualityStandardsPage = ({ navigateHome }) => (
  <PageLayout title="Quality Standards" navigateHome={navigateHome}>
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Precision Standards</h3>
        <p className="text-slate-600 mb-4">
          We maintain strict quality control processes to ensure every print meets our high standards:
        </p>
        <ul className="text-slate-600 space-y-2">
          <li>• Dimensional accuracy: ±0.1mm for most geometries</li>
          <li>• Surface finish: Ra 6.3μm standard, Ra 1.6μm with post-processing</li>
          <li>• Layer adhesion: &gt;90% of material strength</li>
          <li>• Color consistency: Delta E &lt;2 for colored materials</li>
        </ul>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Quality Control Process</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-[#0007b5]">1</span>
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">Pre-Print Inspection</h4>
            <p className="text-slate-600 text-sm">File analysis and optimization review</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-[#0007b5]">2</span>
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">Print Monitoring</h4>
            <p className="text-slate-600 text-sm">Real-time quality monitoring during production</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-[#0007b5]">3</span>
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">Final Inspection</h4>
            <p className="text-slate-600 text-sm">Dimensional verification and finish quality check</p>
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
);

// Shipping Info Page
const ShippingInfoPage = ({ navigateHome }) => (
  <PageLayout title="Shipping & Returns" navigateHome={navigateHome}>
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Delivery Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-700">Standard Shipping</CardTitle>
              <CardDescription>4-6 weeks delivery</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Economical shipping option for non-urgent projects. Includes tracking and insurance coverage.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-green-700">Express Shipping</CardTitle>
              <CardDescription>2-3 weeks delivery</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Expedited processing and shipping for time-sensitive projects. Priority handling throughout production.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Packaging</h3>
        <p className="text-slate-600">
          All parts are carefully packaged with protective materials to ensure safe delivery. 
          Fragile or complex geometries receive additional padding and custom packaging as needed.
        </p>
      </div>
    </div>
  </PageLayout>
);

// Marketplace Route Components

// Designer Application Route

// Creator Profile Route
const CreatorProfileRoute = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [creator, setCreator] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  
  // Edit form state
  const [bio, setBio] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [banner, setBanner] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Check if current user is the creator
  const isOwnProfile = user && creator && user.id === creator.user_id;

  useEffect(() => {
    fetchCreatorProfile();
  }, [username]);

  const fetchCreatorProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/creators/${username}`);
      if (response.ok) {
        const data = await response.json();
        setCreator(data.creator);
        setListings(data.listings);
        setBio(data.creator.bio || '');
      } else {
        console.error('Creator not found');
      }
    } catch (error) {
      console.error('Error fetching creator:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Upload profile picture if selected
      if (profilePicture) {
        const formData = new FormData();
        formData.append('file', profilePicture);
        
        await fetch(`${BACKEND_URL}/api/creator/upload-profile-picture`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      }

      // Upload banner if selected
      if (banner) {
        const formData = new FormData();
        formData.append('file', banner);
        
        await fetch(`${BACKEND_URL}/api/creator/upload-banner`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      }

      // Update bio
      const bioFormData = new FormData();
      bioFormData.append('bio', bio);

      const response = await fetch(`${BACKEND_URL}/api/creator/my-profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: bioFormData
      });

      if (response.ok) {
        setIsEditing(false);
        fetchCreatorProfile(); // Refresh profile
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading creator profile...</p>
        </div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Creator Not Found</h1>
          <p className="text-gray-600 mb-4">This creator profile doesn't exist.</p>
          <Button onClick={() => navigate('/marketplace')}>Back to Marketplace</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner */}
      <div className="relative h-64 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
        {creator.banner_url && (
          <img 
            src={`${BACKEND_URL}${creator.banner_url}`} 
            alt="Banner" 
            className="w-full h-full object-cover"
          />
        )}
        {isEditing && (
          <div className="absolute bottom-4 right-4">
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setBanner(e.target.files[0])}
              className="bg-white"
            />
          </div>
        )}
      </div>

      {/* Profile Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative -mt-20 pb-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Profile Picture */}
              <div className="relative">
                {creator.profile_picture_url ? (
                  <img 
                    src={`${BACKEND_URL}${creator.profile_picture_url}`}
                    alt={creator.display_name}
                    className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">
                      {creator.display_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {isEditing && (
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setProfilePicture(e.target.files[0])}
                    className="mt-2"
                  />
                )}
              </div>

              {/* Creator Info */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">{creator.display_name}</h1>
                  {isOwnProfile && !isEditing && (
                    <Button onClick={() => setIsEditing(true)} variant="outline">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  )}
                  {isEditing && (
                    <div className="flex gap-2">
                      <Button onClick={handleSaveProfile} disabled={uploading}>
                        {uploading ? 'Saving...' : 'Save'}
                      </Button>
                      <Button onClick={() => setIsEditing(false)} variant="outline">
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-gray-600 mb-4">@{creator.username}</p>

                {/* Bio */}
                {isEditing ? (
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell us about yourself and your work..."
                    className="w-full p-3 border rounded-lg resize-none"
                    rows={4}
                  />
                ) : (
                  creator.bio && (
                    <p className="text-gray-700 leading-relaxed mb-4">{creator.bio}</p>
                  )
                )}

                {/* Stats */}
                <div className="flex gap-6 mt-4">
                  <div>
                    <span className="block text-2xl font-bold text-gray-900">{listings.length}</span>
                    <span className="text-sm text-gray-600">Designs</span>
                  </div>
                  <div>
                    <span className="block text-2xl font-bold text-gray-900">{creator.total_sales || 0}</span>
                    <span className="text-sm text-gray-600">Sales</span>
                  </div>
                  <div>
                    <span className="block text-2xl font-bold text-gray-900">
                      {new Date(creator.member_since).getFullYear()}
                    </span>
                    <span className="text-sm text-gray-600">Member Since</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Designs Grid */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Designs by {creator.display_name}</h2>
          
          {listings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No designs available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {listings.map((listing) => (
                <Card 
                  key={listing.id} 
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                >
                  <div className="relative aspect-square bg-gradient-to-br from-blue-100 to-purple-100">
                    {listing.preview_images && listing.preview_images.length > 0 ? (
                      <img 
                        src={listing.preview_images[0]} 
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-blue-500" />
                      </div>
                    )}
                    {listing.has_3d_preview && (
                      <Badge className="absolute top-2 right-2 bg-[#0007b5] text-white text-xs">3D</Badge>
                    )}
                    {listing.listing_type === 'assembly' && (
                      <Badge className="absolute top-2 left-2 bg-purple-600 text-white text-xs">Assembly</Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{listing.title}</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Personal</p>
                        <p className="font-bold text-[#0007b5]">${listing.personal_price}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Commercial</p>
                        <p className="font-bold text-[#0007b5]">${listing.commercial_price}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};


const DesignerApplyRoute = () => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [formData, setFormData] = useState({
    designer_name: '',
    display_name: '',
    bio: '',
    portfolio_links: ['']
  });
  const [sampleFiles, setSampleFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const addPortfolioLink = () => {
    setFormData(prev => ({
      ...prev,
      portfolio_links: [...prev.portfolio_links, '']
    }));
  };

  const removePortfolioLink = (index) => {
    setFormData(prev => ({
      ...prev,
      portfolio_links: prev.portfolio_links.filter((_, i) => i !== index)
    }));
  };

  const updatePortfolioLink = (index, value) => {
    setFormData(prev => ({
      ...prev,
      portfolio_links: prev.portfolio_links.map((link, i) => i === index ? value : link)
    }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setSampleFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setSubmitStatus({ type: 'error', message: 'Please sign in to apply as a designer.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Use proper backend URL from environment variable
      const API_BASE = BACKEND_URL;
      const token = await getIdToken();

      const applicationData = {
        designer_name: formData.designer_name,
        bio: formData.bio,
        portfolio_links: formData.portfolio_links.filter(link => link.trim() !== '')
      };

      const response = await fetch(`${API_BASE}/api/marketplace/apply-designer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(applicationData)
      });

      const result = await response.json();

      if (response.ok) {
        // Track designer application in Google Analytics
        trackDesignerApplication();
        
        setSubmitStatus({
          type: 'success',
          message: 'Application submitted successfully! We\'ll review your application and get back to you within 2-3 business days.'
        });
        setFormData({ designer_name: '', display_name: '', bio: '', portfolio_links: [''] });
        setSampleFiles([]);
      } else {
        throw new Error(result.detail || 'Application submission failed');
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to submit application. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center space-x-3"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-[#0007b5] to-[#000690] bg-clip-text text-transparent">
                Create 3D Works
              </h1>
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800 transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Apply to Become a Designer</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Join our marketplace and sell your 3D designs to customers worldwide. 
            Professional platform with global reach and secure payments.
          </p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-[#0007b5]">Designer Application</CardTitle>
            <CardDescription>
              Tell us about yourself and your 3D design experience. We'll review your application within 2-3 business days.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="designer_name">Designer Name *</Label>
                <Input
                  id="designer_name"
                  type="text"
                  placeholder="Your professional name or studio name"
                  value={formData.designer_name}
                  onChange={(e) => setFormData(prev => ({...prev, designer_name: e.target.value}))}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="display_name">Display Name (Optional)</Label>
                <Input
                  id="display_name"
                  type="text"
                  placeholder="Business name or brand name for marketplace listings"
                  value={formData.display_name || ''}
                  onChange={(e) => setFormData(prev => ({...prev, display_name: e.target.value}))}
                  className="mt-2"
                />
                <p className="text-sm text-slate-500 mt-1">
                  Optional: Use a business name or brand that will appear on your marketplace listings. Leave blank to use your designer name.
                </p>
              </div>

              <div>
                <Label htmlFor="bio">Biography *</Label>
                <Textarea
                  id="bio"
                  placeholder="Tell us about your 3D design experience, specializations, and background..."
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({...prev, bio: e.target.value}))}
                  required
                  rows={6}
                  className="mt-2"
                />
                <p className="text-sm text-slate-500 mt-1">
                  Include your experience, specializations, and what makes your designs unique.
                </p>
              </div>

              <div>
                <Label>Portfolio Links (Optional)</Label>
                <div className="space-y-3 mt-2">
                  {formData.portfolio_links.map((link, index) => (
                    <div key={index} className="flex space-x-2">
                      <Input
                        type="url"
                        placeholder="https://your-portfolio.com or https://github.com/username"
                        value={link}
                        onChange={(e) => updatePortfolioLink(index, e.target.value)}
                        className="flex-1"
                      />
                      {formData.portfolio_links.length > 1 && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => removePortfolioLink(index)}
                          className="px-3"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={addPortfolioLink}
                    className="w-full"
                  >
                    Add Another Portfolio Link
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="sample_files">Sample Files (Optional)</Label>
                <Input
                  id="sample_files"
                  type="file"
                  accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                  onChange={handleFileUpload}
                  multiple
                  className="mt-2"
                />
                <p className="text-sm text-slate-500 mt-1">
                  Upload 1-3 sample CAD files to showcase your work. Max 50MB per file.
                </p>
                {sampleFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {sampleFiles.map((file, index) => (
                      <Badge key={index} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        ✓ {file.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-semibold text-blue-900 mb-3">Designer Benefits</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex items-center">
                    <span>✓ Global marketplace reach</span>
                  </div>
                  <div className="flex items-center">
                    <span>✓ Secure payment processing</span>
                  </div>
                  <div className="flex items-center">
                    <span>✓ Marketing support for your designs</span>
                  </div>
                  <div className="pt-2 mt-3 border-t border-blue-200">
                    <p>Revenue details will be shared upon application approval.</p>
                  </div>
                </div>
              </div>

              {submitStatus && (
                <div className={`rounded-lg p-4 ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : submitStatus.type === 'warning'
                    ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <Button 
                type="submit" 
                disabled={isSubmitting || !user}
                className="w-full bg-[#0007b5] hover:bg-[#000690] py-3 text-lg font-semibold"
              >
                {isSubmitting ? 'Submitting Application...' : 'Submit Designer Application'}
              </Button>

              {!user && (
                <p className="text-center text-slate-600">
                  Please <button onClick={() => navigate('/')} className="text-[#0007b5] hover:underline">sign in</button> to submit your application.
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Designer Dashboard Route
const DesignerDashboardRoute = () => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) return;

      try {
        // Use proper backend URL from environment variable
        const API_BASE = BACKEND_URL;
        const token = await getIdToken();

        // Fetch dashboard data
        const dashboardResponse = await fetch(`${API_BASE}/api/marketplace/designer/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (dashboardResponse.ok) {
          const dashboardResult = await dashboardResponse.json();
          setDashboardData(dashboardResult);
        }

        // Fetch designer listings
        const listingsResponse = await fetch(`${API_BASE}/api/marketplace/designer/listings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (listingsResponse.ok) {
          const listingsResult = await listingsResponse.json();
          setListings(listingsResult.listings || []);
        }

      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center p-6">
            <h2 className="text-xl font-semibold mb-4">Access Required</h2>
            <p className="text-gray-600 mb-4">Please sign in to access the designer dashboard.</p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Designer Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                onClick={() => navigate('/designer/upload')}
                className="bg-[#0007b5] hover:bg-[#000690]"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Design
              </Button>
              <Button onClick={() => navigate('/')} variant="outline">
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${dashboardData?.earnings?.total_earnings?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600">$</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Listings</p>
                  <p className="text-2xl font-bold text-[#0007b5]">
                    {dashboardData?.active_listings || 0}
                  </p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#0007b5]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Downloads</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {dashboardData?.earnings?.total_downloads || 0}
                  </p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Upload className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending Payout</p>
                  <p className="text-2xl font-bold text-orange-600">
                    ${dashboardData?.earnings?.pending_payout?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-orange-600">⏳</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* My Listings */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>My Listings</CardTitle>
              <Button 
                onClick={() => navigate('/designer/upload')}
                className="bg-[#0007b5] hover:bg-[#000690]"
              >
                <Upload className="w-4 h-4 mr-2" />
                Add New Design
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {listings.length === 0 ? (
              <div className="text-center py-12">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No listings yet</h3>
                <p className="text-gray-600 mb-4">Upload your first 3D design to start selling!</p>
                <Button onClick={() => navigate('/designer/upload')}>
                  Upload Your First Design
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.map((listing) => (
                  <Card key={listing.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-4">
                      <div className="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                        <Package className="w-12 h-12 text-gray-400" />
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">{listing.title}</h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{listing.description}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Personal:</span>
                          <span className="font-semibold">${listing.personal_price}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Commercial:</span>
                          <span className="font-semibold">${listing.commercial_price}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Downloads:</span>
                          <span>{listing.download_count}</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex items-center justify-between">
                          <Badge 
                            variant={listing.status === 'active' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {listing.status}
                          </Badge>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex space-x-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => navigate(`/designer/edit/${listing.id}`)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Designer Upload Route
const DesignerUploadRoute = () => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    use_case: '',
    personal_price: '',
    commercial_price: '',
    is_printable: true,
    tags: '',
    dimensions: '',
    dimensions_unit: 'mm',
    dimensions_unit_custom: '',
    complexity_level: ''
  });
  const [listingType, setListingType] = useState('individual'); // 'individual' or 'assembly'
  const [pricingTier, setPricingTier] = useState('premium'); // 'free' or 'premium'
  const [cadFile, setCadFile] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [hideCadFile, setHideCadFile] = useState(false);
  
  // Assembly-specific state
  const [assemblyComponents, setAssemblyComponents] = useState([]);
  const [assemblyPreview, setAssemblyPreview] = useState(null);
  const [assemblyPreviewType, setAssemblyPreviewType] = useState('image'); // 'image' or 'cad_file'
  const [showComponentFiles, setShowComponentFiles] = useState(true);
  const [showComponentNames, setShowComponentNames] = useState(true);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const categories = [
    'Automotive', 'Home & Living', 'Garden & Outdoor', 'Organization', 
    'Electronics', 'Toys & Games', 'Art & Decorative', 'Tools & Hardware',
    'Fashion & Jewelry', 'Replacement Parts', 'Sim Racing', 'Flight Simulator', 'Other'
  ];

  const useCases = ['Functional', 'Decorative', 'Replacement', 'Educational', 'Prototype'];

  const handleCadFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.size <= 200 * 1024 * 1024) { // 200MB limit
      setCadFile(file);
    } else {
      alert('File size must be under 200MB');
    }
  };

  const handlePreviewImagesUpload = (event) => {
    const files = Array.from(event.target.files);
    setPreviewImages(files.slice(0, 5)); // Max 5 images
  };

  const handleAssemblyComponentsUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length < 2) {
      alert('Assembly must have at least 2 components');
      return;
    }
    if (files.length > 20) {
      alert('Maximum 20 components allowed');
      return;
    }
    
    // Create component objects with editable display names
    const components = files.map((file, index) => ({
      file: file,
      displayName: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      order: index
    }));
    setAssemblyComponents(components);
  };

  const handleAssemblyPreviewUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAssemblyPreview(file);
    }
  };

  const updateComponentName = (index, newName) => {
    const updated = [...assemblyComponents];
    updated[index].displayName = newName;
    setAssemblyComponents(updated);
  };

  const removeComponent = (index) => {
    const updated = assemblyComponents.filter((_, i) => i !== index);
    setAssemblyComponents(updated);
  };

  const moveComponent = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === assemblyComponents.length - 1) return;
    
    const updated = [...assemblyComponents];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    
    // Update order indices
    updated.forEach((comp, i) => comp.order = i);
    setAssemblyComponents(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    // Validate based on listing type
    if (listingType === 'individual') {
      // Individual listing needs CAD file
      if (!cadFile) {
        setSubmitStatus({
          type: 'error',
          message: 'CAD file is required for individual listings.'
        });
        return;
      }

      // Validate images are required if CAD file is hidden
      if (hideCadFile && previewImages.length === 0) {
        setSubmitStatus({
          type: 'error',
          message: 'Preview images are required when CAD file is hidden from the listing.'
        });
        return;
      }
    } else {
      // Assembly listing needs components and preview
      if (assemblyComponents.length < 2) {
        setSubmitStatus({
          type: 'error',
          message: 'Assembly must have at least 2 component files.'
        });
        return;
      }

      if (!assemblyPreview) {
        setSubmitStatus({
          type: 'error',
          message: 'Assembly preview (image or CAD file) is required.'
        });
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Use proper backend URL from environment variable
      const API_BASE = BACKEND_URL;
      const token = await getIdToken();

      const listingData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        use_case: formData.use_case,
        pricing_tier: pricingTier,
        personal_price: parseFloat(formData.personal_price),
        commercial_price: parseFloat(formData.commercial_price),
        print_markup_percentage: 0.0,
        is_printable: formData.is_printable,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        dimensions: formData.dimensions,
        dimensions_unit: formData.dimensions_unit === 'other' ? formData.dimensions_unit_custom : formData.dimensions_unit,
        complexity_level: formData.complexity_level,
        preview_type: hideCadFile ? 'images' : (previewImages.length > 0 ? 'images' : '3d_model'),
        hide_cad_file: hideCadFile,
        listing_type: listingType,
        assembly_preview_type: listingType === 'assembly' ? assemblyPreviewType : 'none',
        show_component_files: showComponentFiles,
        show_component_names: showComponentNames
      };

      // Step 1: Create the listing
      const response = await fetch(`${API_BASE}/api/marketplace/listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(listingData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Failed to create listing');
      }

      const listingId = result.listing_id;
      let uploadErrors = [];

      // Step 2: Upload CAD file if provided
      if (cadFile) {
        try {
          const cadFormData = new FormData();
          cadFormData.append('file', cadFile);

          const cadResponse = await fetch(`${API_BASE}/api/marketplace/upload-cad-file?listing_id=${listingId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: cadFormData
          });

          if (!cadResponse.ok) {
            const cadError = await cadResponse.json();
            uploadErrors.push(`CAD file upload failed: ${cadError.detail || 'Unknown error'}`);
          }
        } catch (error) {
          uploadErrors.push(`CAD file upload error: ${error.message}`);
        }
      }

      // Step 3: Upload assembly components if it's an assembly
      if (listingType === 'assembly' && assemblyComponents.length > 0) {
        try {
          const componentFormData = new FormData();
          assemblyComponents.forEach(comp => {
            componentFormData.append('files', comp.file);
          });
          
          // Pass component display names as JSON
          const displayNames = assemblyComponents.map(comp => comp.displayName);
          componentFormData.append('component_names', JSON.stringify(displayNames));
          componentFormData.append('listing_id', listingId);

          const componentResponse = await fetch(`${API_BASE}/api/marketplace/upload-assembly-components`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: componentFormData
          });

          if (!componentResponse.ok) {
            const componentError = await componentResponse.json();
            uploadErrors.push(`Assembly components upload failed: ${componentError.detail || 'Unknown error'}`);
          }
        } catch (error) {
          uploadErrors.push(`Assembly components upload error: ${error.message}`);
        }
      }

      // Step 4: Upload assembly preview if provided
      if (listingType === 'assembly' && assemblyPreview) {
        try {
          const previewFormData = new FormData();
          previewFormData.append('file', assemblyPreview);
          previewFormData.append('listing_id', listingId);
          previewFormData.append('preview_type', assemblyPreviewType);

          const previewResponse = await fetch(`${API_BASE}/api/marketplace/upload-assembly-preview`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: previewFormData
          });

          if (!previewResponse.ok) {
            const previewError = await previewResponse.json();
            uploadErrors.push(`Assembly preview upload failed: ${previewError.detail || 'Unknown error'}`);
          }
        } catch (error) {
          uploadErrors.push(`Assembly preview upload error: ${error.message}`);
        }
      }

      // Step 5: Upload preview images if provided (for individual listings)
      if (previewImages.length > 0) {
        try {
          const imageFormData = new FormData();
          previewImages.forEach(file => {
            imageFormData.append('files', file);
          });

          const imageResponse = await fetch(`${API_BASE}/api/marketplace/upload-preview-images?listing_id=${listingId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: imageFormData
          });

          if (!imageResponse.ok) {
            const imageError = await imageResponse.json();
            uploadErrors.push(`Preview images upload failed: ${imageError.detail || 'Unknown error'}`);
          }
        } catch (error) {
          uploadErrors.push(`Preview images upload error: ${error.message}`);
        }
      }

      // Show success or partial success message
      if (uploadErrors.length === 0) {
        // Track design upload in Google Analytics
        trackDesignUpload({
          title: formData.title,
          category: formData.category,
          is_printable: formData.is_printable
        });
        
        setSubmitStatus({
          type: 'success',
          message: 'Design uploaded successfully! It will be reviewed and published within 24 hours.'
        });
      } else {
        setSubmitStatus({
          type: 'warning',
          message: `Listing created but some files failed to upload: ${uploadErrors.join(', ')}. You can try uploading files again later.`
        });
      }

      // Reset form on success
      setFormData({
        title: '', description: '', category: '', use_case: '',
        personal_price: '', commercial_price: '',
        is_printable: true, tags: '', dimensions: '', 
        dimensions_unit: 'mm', dimensions_unit_custom: '', complexity_level: ''
      });
      setCadFile(null);
      setPreviewImages([]);
      setAssemblyComponents([]);
      setAssemblyPreview(null);
      setListingType('individual');
      setPricingTier('premium');
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to upload design. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center p-6">
            <h2 className="text-xl font-semibold mb-4">Access Required</h2>
            <p className="text-gray-600 mb-4">Please sign in to upload designs.</p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Upload New Design</h1>
            </div>
            <Button onClick={() => navigate('/designer/dashboard')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Add New Marketplace Listing</CardTitle>
            <CardDescription>
              Upload your 3D design with details and pricing to sell in the marketplace.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Listing Type Selector */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <Label className="text-base font-semibold mb-3 block">Listing Type *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setListingType('individual')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      listingType === 'individual' 
                        ? 'border-blue-500 bg-blue-100' 
                        : 'border-gray-300 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="font-semibold text-lg mb-1">Individual Part</div>
                    <div className="text-sm text-gray-600">Single 3D file for one component or object</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setListingType('assembly')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      listingType === 'assembly' 
                        ? 'border-blue-500 bg-blue-100' 
                        : 'border-gray-300 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="font-semibold text-lg mb-1">Assembly</div>
                    <div className="text-sm text-gray-600">Multiple parts that form a complete product</div>
                  </button>
                </div>
              </div>

              {/* Pricing Tier Selector */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <Label className="text-base font-semibold mb-3 block">Pricing Tier *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setPricingTier('free')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      pricingTier === 'free' 
                        ? 'border-green-500 bg-green-100' 
                        : 'border-gray-300 bg-white hover:border-green-300'
                    }`}
                  >
                    <div className="font-semibold text-lg mb-1">Free</div>
                    <div className="text-sm text-gray-600">Available for free download</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPricingTier('premium')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      pricingTier === 'premium' 
                        ? 'border-green-500 bg-green-100' 
                        : 'border-gray-300 bg-white hover:border-green-300'
                    }`}
                  >
                    <div className="font-semibold text-lg mb-1">Premium</div>
                    <div className="text-sm text-gray-600">Paid design with licensing options</div>
                  </button>
                </div>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="title">Design Title *</Label>
                  <Input
                    id="title"
                    type="text"
                    placeholder="Modern Desk Organizer"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData(prev => ({...prev, category: value}))} 
                    required
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choose category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your design, its features, recommended use cases, and printing requirements..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                  required
                  rows={4}
                  className="mt-2"
                />
              </div>

              {/* File Uploads Section */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">File Uploads</h3>
                
                {/* Individual Part File Upload */}
                {listingType === 'individual' && (
                  <>
                    <div className="mb-6">
                      <Label htmlFor="cad_file">3D CAD File (Required - Max 200MB) *</Label>
                      <Input
                        id="cad_file"
                        type="file"
                        accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                        onChange={handleCadFileUpload}
                        className="mt-2"
                        required
                      />
                      <p className="text-xs text-slate-600 mt-1">
                        CAD file is required for manufacturing and print cost calculations
                      </p>
                      {cadFile && (
                        <Badge className="mt-2 bg-green-50 text-green-700 border-green-200">
                          ✓ {cadFile.name}
                        </Badge>
                      )}
                    </div>

                    <div className="mb-6 bg-white rounded-lg p-4 border border-blue-200">
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="hide_cad_file"
                          checked={hideCadFile}
                          onChange={(e) => setHideCadFile(e.target.checked)}
                          className="w-4 h-4 text-[#0007b5] border-gray-300 rounded focus:ring-blue-500 mt-1"
                        />
                        <div className="ml-3">
                          <label htmlFor="hide_cad_file" className="font-medium text-slate-900 cursor-pointer">
                            Hide CAD file from listing
                          </label>
                          <p className="text-xs text-slate-600 mt-1">
                            Check this if you don't want customers to see the 3D preview. 
                            <strong className="text-orange-600"> If checked, preview images are required.</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="preview_images">
                        Preview Images {hideCadFile ? '(Required - Max 5) *' : '(Optional - Max 5)'}
                      </Label>
                      <Input
                        id="preview_images"
                        type="file"
                        accept="image/*"
                        onChange={handlePreviewImagesUpload}
                        multiple
                        className="mt-2"
                        required={hideCadFile}
                      />
                      <p className="text-xs text-slate-600 mt-1">
                        {hideCadFile 
                          ? 'Images required when CAD file is hidden'
                          : 'Upload images to complement the 3D preview'
                        }
                      </p>
                      {previewImages.length > 0 && (
                        <div className="mt-2 space-x-2">
                          {previewImages.map((file, index) => (
                            <Badge key={index} className="bg-blue-50 text-blue-700 border-blue-200">
                              {file.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {cadFile && !hideCadFile && (
                      <div className="mt-6">
                        <Label>3D Model Preview</Label>
                        <div className="mt-2 border rounded-lg p-4 bg-white">
                          <ThreeDPreview 
                            file={cadFile} 
                            onAnalysis={(analysisData) => {
                              if (analysisData.dimensions) {
                                setFormData(prev => ({
                                  ...prev,
                                  dimensions: `${analysisData.dimensions.x} x ${analysisData.dimensions.y} x ${analysisData.dimensions.z}`,
                                  dimensions_unit: analysisData.dimensions.unit || 'mm'
                                }));
                              }
                            }}
                            className="w-full"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Assembly File Upload */}
                {listingType === 'assembly' && (
                  <>
                    <div className="mb-6">
                      <Label htmlFor="assembly_components">Assembly Component Files (2-20 files, Max 100MB each) *</Label>
                      <Input
                        id="assembly_components"
                        type="file"
                        accept=".stl,.obj,.3mf,.ply,.step,.stp"
                        onChange={handleAssemblyComponentsUpload}
                        multiple
                        className="mt-2"
                        required
                      />
                      <p className="text-xs text-slate-600 mt-1">
                        Upload all CAD files that make up your assembly (minimum 2 parts required)
                      </p>
                    </div>

                    {assemblyComponents.length > 0 && (
                      <div className="mb-6 border rounded-lg p-4 bg-gray-50">
                        <Label className="mb-3 block">Component Files ({assemblyComponents.length})</Label>
                        <div className="space-y-2">
                          {assemblyComponents.map((comp, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 bg-white rounded border">
                              <span className="text-sm font-mono text-gray-600">{index + 1}.</span>
                              <Input
                                value={comp.displayName}
                                onChange={(e) => updateComponentName(index, e.target.value)}
                                placeholder="Component name"
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => moveComponent(index, 'up')}
                                disabled={index === 0}
                              >
                                ↑
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => moveComponent(index, 'down')}
                                disabled={index === assemblyComponents.length - 1}
                              >
                                ↓
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => removeComponent(index)}
                                className="text-red-600"
                              >
                                ✕
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mb-6">
                      <Label htmlFor="assembly_preview">Assembly Preview (Image or 3D CAD file) *</Label>
                      <div className="mt-2 space-y-2">
                        <div className="flex gap-4 mb-2">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="image"
                              checked={assemblyPreviewType === 'image'}
                              onChange={(e) => setAssemblyPreviewType(e.target.value)}
                              className="mr-2"
                            />
                            Image
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="cad_file"
                              checked={assemblyPreviewType === 'cad_file'}
                              onChange={(e) => setAssemblyPreviewType(e.target.value)}
                              className="mr-2"
                            />
                            3D CAD File
                          </label>
                        </div>
                        <Input
                          id="assembly_preview"
                          type="file"
                          accept={assemblyPreviewType === 'image' ? 'image/*' : '.stl,.obj,.3mf,.step,.stp'}
                          onChange={handleAssemblyPreviewUpload}
                          className="mt-2"
                          required
                        />
                        <p className="text-xs text-slate-600">
                          {assemblyPreviewType === 'image' 
                            ? 'Upload an image showing the complete assembly'
                            : 'Upload a 3D CAD file of the complete assembled product'
                          }
                        </p>
                        {assemblyPreview && (
                          <Badge className="mt-2 bg-green-50 text-green-700 border-green-200">
                            ✓ {assemblyPreview.name}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="mb-6 bg-white rounded-lg p-4 border border-blue-200 space-y-3">
                      <Label className="block font-semibold">Component Visibility Settings</Label>
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="show_component_files"
                          checked={showComponentFiles}
                          onChange={(e) => setShowComponentFiles(e.target.checked)}
                          className="w-4 h-4 text-[#0007b5] border-gray-300 rounded focus:ring-blue-500 mt-1"
                        />
                        <div className="ml-3">
                          <label htmlFor="show_component_files" className="font-medium text-slate-900 cursor-pointer">
                            Show individual component 3D previews
                          </label>
                          <p className="text-xs text-slate-600 mt-1">
                            Allow customers to view each component's 3D model individually
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="show_component_names"
                          checked={showComponentNames}
                          onChange={(e) => setShowComponentNames(e.target.checked)}
                          className="w-4 h-4 text-[#0007b5] border-gray-300 rounded focus:ring-blue-500 mt-1"
                        />
                        <div className="ml-3">
                          <label htmlFor="show_component_names" className="font-medium text-slate-900 cursor-pointer">
                            Show component names in listing
                          </label>
                          <p className="text-xs text-slate-600 mt-1">
                            Display the list of component names to customers
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* File Requirements Notice */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                  <div className="flex items-start">
                    <div className="text-yellow-600 mr-2">⚠️</div>
                    <div className="text-sm text-yellow-800">
                      <strong>File Requirements:</strong>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>CAD file is always required</li>
                        <li>If you hide the CAD file, preview images become required</li>
                        <li>If you don't hide the CAD file, customers will see interactive 3D preview</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Pricing & Licensing</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="personal_price">Personal Use Price *</Label>
                    <div className="relative mt-2">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        id="personal_price"
                        type="number"
                        step="0.01"
                        min="1"
                        placeholder="9.99"
                        value={formData.personal_price}
                        onChange={(e) => setFormData(prev => ({...prev, personal_price: e.target.value}))}
                        className="pl-8"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="commercial_price">Commercial Use Price *</Label>
                    <div className="relative mt-2">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        id="commercial_price"
                        type="number"
                        step="0.01"
                        min="1"
                        placeholder="49.99"
                        value={formData.commercial_price}
                        onChange={(e) => setFormData(prev => ({...prev, commercial_price: e.target.value}))}
                        className="pl-8"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="use_case">Use Case *</Label>
                  <Select 
                    value={formData.use_case} 
                    onValueChange={(value) => setFormData(prev => ({...prev, use_case: value}))} 
                    required
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choose use case" />
                    </SelectTrigger>
                    <SelectContent>
                      {useCases.map(useCase => (
                        <SelectItem key={useCase} value={useCase}>{useCase}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="dimensions">Dimensions (Optional)</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="dimensions"
                      type="text"
                      placeholder="120 x 80 x 15"
                      value={formData.dimensions}
                      onChange={(e) => setFormData(prev => ({...prev, dimensions: e.target.value}))}
                      className="flex-1"
                    />
                    <Select 
                      value={formData.dimensions_unit} 
                      onValueChange={(value) => setFormData(prev => ({...prev, dimensions_unit: value}))}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mm">mm</SelectItem>
                        <SelectItem value="cm">cm</SelectItem>
                        <SelectItem value="in">in</SelectItem>
                        <SelectItem value="ft">ft</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.dimensions_unit === 'other' && (
                    <Input
                      type="text"
                      placeholder="Enter custom unit"
                      value={formData.dimensions_unit_custom}
                      onChange={(e) => setFormData(prev => ({...prev, dimensions_unit_custom: e.target.value}))}
                      className="mt-2"
                    />
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="tags">Tags (Comma separated)</Label>
                <Input
                  id="tags"
                  type="text"
                  placeholder="organizer, desk, office, storage"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({...prev, tags: e.target.value}))}
                  className="mt-2"
                />
              </div>

              {submitStatus && (
                <div className={`rounded-lg p-4 ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : submitStatus.type === 'warning'
                    ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-[#0007b5] hover:bg-[#000690] py-3 text-lg font-semibold"
              >
                {isSubmitting ? 'Uploading Design...' : 'Upload Design to Marketplace'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Admin Marketplace Route
const AdminMarketplaceRoute = () => {
  const navigate = useNavigate();
  const { user, getIdToken, testLogin } = useAuth();
  const [applications, setApplications] = useState([]);
  const [listings, setListings] = useState([]);
  const [activeTab, setActiveTab] = useState('applications');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        // Use proper backend URL from environment variable
        const API_BASE = BACKEND_URL;
        const token = await getIdToken();

        // Fetch designer applications
        const appsResponse = await fetch(`${API_BASE}/api/marketplace/applications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (appsResponse.ok) {
          const appsResult = await appsResponse.json();
          setApplications(appsResult.applications || []);
        }

        // Fetch all listings
        const listingsResponse = await fetch(`${API_BASE}/api/marketplace/listings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (listingsResponse.ok) {
          const listingsResult = await listingsResponse.json();
          setListings(listingsResult.listings || []);
        }

      } catch (error) {
        console.error('Admin data fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const reviewApplication = async (applicationId, status, adminNotes = '') => {
    try {
      // Use proper backend URL from environment variable
      const API_BASE = BACKEND_URL;
      const token = await getIdToken();

      const response = await fetch(`${API_BASE}/api/marketplace/applications/${applicationId}?status=${status}&admin_notes=${encodeURIComponent(adminNotes)}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Refresh applications
        const appsResponse = await fetch(`${API_BASE}/api/marketplace/applications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (appsResponse.ok) {
          const appsResult = await appsResponse.json();
          setApplications(appsResult.applications || []);
        }
      }
    } catch (error) {
      console.error('Review application error:', error);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center p-6">
            <h2 className="text-xl font-semibold mb-4">Admin Access Required</h2>
            <p className="text-gray-600 mb-4">Please sign in with admin credentials.</p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/')}>Go to Home</Button>
              {/* Temporary Test Admin Access - Development Only */}
              <Button 
                onClick={testLogin}
                className="bg-orange-600 hover:bg-orange-700 w-full"
              >
                🔧 Test Admin Access (Dev Only)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Marketplace Admin</h1>
            </div>
            <Button onClick={() => navigate('/admin')} variant="outline">
              Back to Admin
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="applications">Designer Applications</TabsTrigger>
            <TabsTrigger value="listings">Marketplace Listings</TabsTrigger>
          </TabsList>

          <TabsContent value="applications" className="mt-8">
            <Card>
              <CardHeader>
                <CardTitle>Designer Applications</CardTitle>
                <CardDescription>
                  Review and approve designer applications to join the marketplace.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading applications...</p>
                  </div>
                ) : applications.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No applications found.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {applications.map((app) => (
                      <Card key={app.id} className="border-l-4 border-l-blue-500">
                        <CardContent className="p-6">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2">
                              <div className="flex items-start justify-between mb-4">
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">{app.designer_name}</h3>
                                  <p className="text-gray-600">{app.user_email}</p>
                                </div>
                                <Badge 
                                  variant={app.status === 'pending' ? 'secondary' : 
                                          app.status === 'approved' ? 'default' : 'destructive'}
                                >
                                  {app.status}
                                </Badge>
                              </div>
                              
                              <div className="space-y-3">
                                <div>
                                  <Label className="font-medium text-gray-700">Bio</Label>
                                  <p className="text-gray-600 text-sm mt-1">{app.bio}</p>
                                </div>
                                
                                {app.portfolio_links.length > 0 && (
                                  <div>
                                    <Label className="font-medium text-gray-700">Portfolio Links</Label>
                                    <div className="mt-1 space-y-1">
                                      {app.portfolio_links.map((link, index) => (
                                        <a 
                                          key={index} 
                                          href={link} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-[#0007b5] hover:underline text-sm block"
                                        >
                                          {link}
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {app.status === 'pending' && (
                              <div className="space-y-3">
                                <div>
                                  <Label className="font-medium text-gray-700">Admin Actions</Label>
                                  <div className="mt-2 space-y-2">
                                    <Button 
                                      onClick={() => reviewApplication(app.id, 'approved', 'Application approved - welcome to the marketplace!')}
                                      className="w-full bg-green-600 hover:bg-green-700"
                                      size="sm"
                                    >
                                      Approve Application
                                    </Button>
                                    <Button 
                                      onClick={() => reviewApplication(app.id, 'rejected', 'Application does not meet current requirements')}
                                      variant="destructive"
                                      className="w-full"
                                      size="sm"
                                    >
                                      Reject Application
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-4 pt-4 border-t text-xs text-gray-500">
                            Applied: {new Date(app.created_at).toLocaleDateString()}
                            {app.reviewed_at && (
                              <span className="ml-4">
                                Reviewed: {new Date(app.reviewed_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="listings" className="mt-8">
            <Card>
              <CardHeader>
                <CardTitle>Marketplace Listings</CardTitle>
                <CardDescription>
                  Manage and moderate marketplace listings.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {listings.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No listings found.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {listings.map((listing) => (
                      <Card key={listing.id} className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-4">
                          <div className="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                            <Package className="w-12 h-12 text-gray-400" />
                          </div>
                          <h3 className="font-semibold text-gray-900 mb-2">{listing.title}</h3>
                          <p className="text-sm text-gray-600 mb-2">
                            by{' '}
                            {listing.creator_username ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/creator/${listing.creator_username}`);
                                }}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {listing.designer_display_name || listing.designer_name}
                              </button>
                            ) : (
                              <span>{listing.designer_display_name || listing.designer_name}</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 mb-3 line-clamp-2">{listing.description}</p>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span>Personal:</span>
                              <span className="font-semibold">${listing.personal_price}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Commercial:</span>
                              <span className="font-semibold">${listing.commercial_price}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Downloads:</span>
                              <span>{listing.download_count}</span>
                            </div>
                          </div>
                          
                          <div className="mt-3 pt-3 border-t flex items-center justify-between">
                            <Badge 
                              variant={listing.status === 'active' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {listing.status}
                            </Badge>
                            <span className="text-xs text-gray-500">{listing.category}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Marketplace Browse Route
const MarketplaceRoute = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    use_case: '',
    listing_type: '',
    pricing_tier: '',
    search: '',
    max_price: ''
  });

  const categories = [
    'Automotive', 'Home & Living', 'Garden & Outdoor', 'Organization', 
    'Electronics', 'Toys & Games', 'Art & Decorative', 'Tools & Hardware',
    'Fashion & Jewelry', 'Replacement Parts', 'Sim Racing', 'Flight Simulator', 'Other'
  ];

  const useCases = ['Functional', 'Decorative', 'Replacement', 'Educational', 'Prototype'];

  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true);
      try {
        // Use proper backend URL from environment variable
        const API_BASE = BACKEND_URL;
        const params = new URLSearchParams();
        
        if (filters.category && filters.category !== 'all-categories') params.append('category', filters.category);
        if (filters.use_case && filters.use_case !== 'all-use-cases') params.append('use_case', filters.use_case);
        if (filters.listing_type && filters.listing_type !== 'all-types') params.append('listing_type', filters.listing_type);
        if (filters.pricing_tier && filters.pricing_tier !== 'all-tiers') params.append('pricing_tier', filters.pricing_tier);
        if (filters.search) params.append('search', filters.search);
        if (filters.max_price) params.append('max_price', filters.max_price);

        const response = await fetch(`${API_BASE}/api/marketplace/listings?${params.toString()}`);
        
        if (response.ok) {
          const result = await response.json();
          setListings(result.listings || []);
        }
      } catch (error) {
        console.error('Fetch listings error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, [filters]);

  const resetFilters = () => {
    setFilters({
      category: '',
      use_case: '',
      listing_type: '',
      pricing_tier: '',
      search: '',
      max_price: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Main Navigation */}
      <MainNavigation 
        onNavigate={(page) => {
          if (page === 'home') navigate('/');
          else if (page === 'services') navigate('/services');
          else if (page === 'products') navigate('/products');
          else if (page === 'marketplace') navigate('/marketplace');
          else if (page === 'about') navigate('/about');
          else if (page === 'contact') navigate('/contact');
          else if (page === 'cart') navigate('/cart');
          else if (page === 'profile') navigate('/profile');
        }} 
        currentPage="marketplace" 
        cartCount={0}
      />

      {/* Hero Banner - Modern Minimalistic Design */}
      <div className="relative bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-14 md:py-18">
            {/* Title with subtle accent */}
            <div className="max-w-4xl">
              <div className="inline-block mb-4">
                <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">3D CAD Library</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-6 page-title-uncage leading-tight">
                Marketplace
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Discover, download, or print professional 3D designs for business or personal use
              </p>
              {!user?.is_designer && (
                <div className="mt-6">
                  <Button onClick={() => navigate('/designer/apply')} className="bg-[#0007b5] hover:bg-[#000690] text-white">
                    Become a Designer
                  </Button>
                </div>
              )}
            </div>
            
            {/* Decorative element - subtle accent line */}
            <div className="mt-8 w-24 h-1 bg-gradient-to-r from-[#0007b5] to-blue-400"></div>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#0007b5] rounded-full blur-3xl"></div>
        </div>
      </div>

      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  type="text"
                  placeholder="Search designs..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({...prev, search: e.target.value}))}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={filters.category} 
                  onValueChange={(value) => setFilters(prev => ({...prev, category: value}))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-categories">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="use_case">Use Case</Label>
                <Select 
                  value={filters.use_case} 
                  onValueChange={(value) => setFilters(prev => ({...prev, use_case: value}))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="All use cases" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-use-cases">All Use Cases</SelectItem>
                    {useCases.map(useCase => (
                      <SelectItem key={useCase} value={useCase}>{useCase}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="listing_type">Type</Label>
                <Select 
                  value={filters.listing_type} 
                  onValueChange={(value) => setFilters(prev => ({...prev, listing_type: value}))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-types">All Types</SelectItem>
                    <SelectItem value="individual">Individual Parts</SelectItem>
                    <SelectItem value="assembly">Assemblies</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="pricing_tier">Pricing</Label>
                <Select 
                  value={filters.pricing_tier} 
                  onValueChange={(value) => setFilters(prev => ({...prev, pricing_tier: value}))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="All pricing" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-tiers">All Pricing</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="max_price">Max Price</Label>
                <Input
                  id="max_price"
                  type="number"
                  placeholder="$50"
                  value={filters.max_price}
                  onChange={(e) => setFilters(prev => ({...prev, max_price: e.target.value}))}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button onClick={resetFilters} variant="outline" size="sm">
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Listings Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading marketplace...</p>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No designs found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your filters or check back later for new designs.</p>
            {!user?.is_designer && (
              <Button onClick={() => navigate('/designer/apply')}>
                Become a Designer
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map((listing) => (
              <Card key={listing.id} className="hover:shadow-xl transition-all duration-300 cursor-pointer group">
                <div onClick={() => navigate(`/marketplace/listing/${listing.id}`)}>
                  <CardContent className="p-0">
                    <div className="aspect-square bg-gradient-to-br from-blue-100 to-purple-100 rounded-t-lg flex items-center justify-center group-hover:from-blue-200 group-hover:to-purple-200 transition-colors relative overflow-hidden">
                      {listing.preview_images && listing.preview_images.length > 0 ? (
                        <img 
                          src={listing.preview_images[0]}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      ) : listing.has_3d_preview ? (
                        <div className="flex flex-col items-center justify-center text-[#0007b5]">
                          <Package className="w-16 h-16 mb-2" />
                          <span className="text-xs font-medium bg-blue-100 px-2 py-1 rounded">3D Model</span>
                        </div>
                      ) : (
                        <Package className="w-16 h-16 text-blue-500" />
                      )}
                      
                      {/* Badges */}
                      <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                        <div className="flex gap-1">
                          {listing.has_3d_preview && (
                            <Badge className="bg-[#0007b5] text-white text-xs">3D</Badge>
                          )}
                          {listing.listing_type === 'assembly' && (
                            <Badge className="bg-purple-600 text-white text-xs">Assembly</Badge>
                          )}
                        </div>
                        {listing.pricing_tier === 'free' && (
                          <Badge className="bg-green-600 text-white text-xs font-semibold">FREE</Badge>
                        )}
                        {listing.pricing_tier === 'premium' && (
                          <Badge className="bg-amber-600 text-white text-xs font-semibold">PREMIUM</Badge>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-[#0007b5] transition-colors">{listing.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">by {listing.designer_display_name || listing.designer_name}</p>
                      <p className="text-xs text-gray-500 mb-3 line-clamp-2">{listing.description}</p>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Personal:</span>
                          <span className="font-semibold text-green-600">{formatPrice(listing.personal_price)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Commercial:</span>
                          <span className="font-semibold text-[#0007b5]">{formatPrice(listing.commercial_price)}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t flex items-center justify-between">
                        <Badge variant="secondary" className="text-xs">
                          {listing.category}
                        </Badge>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <Upload className="w-3 h-3" />
                          <span>{listing.download_count}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Listing Detail Route
const ListingDetailRoute = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const { formatPrice } = useCurrency();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseType, setPurchaseType] = useState('download');
  const [licenseType, setLicenseType] = useState('Personal Use');
  const [purchasing, setPurchasing] = useState(false);
  
  // Print configuration state
  const [printConfig, setPrintConfig] = useState({
    material: 'PLA',
    quality: 'Prototype',  // Valid new option
    strength: 'Cosmetic Only',
    smoothing: 'None - As Printed',
    color: 'Any',
    quantity: 1,
    country_code: 'US',  // Default to US
    postal_code: ''  // Optional postal code
  });
  const [printCost, setPrintCost] = useState(null);
  const [calculatingCost, setCalculatingCost] = useState(false);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        // Use proper backend URL from environment variable  
        const API_BASE = BACKEND_URL;
        const response = await fetch(`${API_BASE}/api/marketplace/listings/${id}`);
        
        if (response.ok) {
          const result = await response.json();
          setListing(result);
          
          // Track design view in Google Analytics
          trackDesignView({
            id: result.id,
            title: result.title,
            category: result.category,
            personal_price: result.personal_price
          });
        } else {
          navigate('/marketplace');
        }
      } catch (error) {
        console.error('Fetch listing error:', error);
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id, navigate]);

  // Calculate print cost when configuration changes
  useEffect(() => {
    const calculatePrintCost = async () => {
      if (!listing || !listing.is_printable || purchaseType !== 'print') {
        setPrintCost(null);
        return;
      }

      setCalculatingCost(true);
      try {
        // Use proper backend URL from environment variable
        const API_BASE = BACKEND_URL;
        const params = new URLSearchParams({
          listing_id: id,
          material: printConfig.material,
          quality: printConfig.quality,
          strength: printConfig.strength,
          smoothing: printConfig.smoothing,
          color: printConfig.color,
          quantity: printConfig.quantity.toString(),
          license_type: licenseType === 'Personal Use' ? 'Personal' : 'Commercial',
          country_code: printConfig.country_code,
          postal_code: printConfig.postal_code || ''
        });
        
        const response = await fetch(`${API_BASE}/api/marketplace/calculate-print-cost?${params.toString()}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const result = await response.json();
          setPrintCost(result);
        } else {
          console.error('Failed to calculate print cost');
          setPrintCost(null);
        }
      } catch (error) {
        console.error('Print cost calculation error:', error);
        setPrintCost(null);
      } finally {
        setCalculatingCost(false);
      }
    };

    calculatePrintCost();
  }, [listing, purchaseType, printConfig, id, licenseType]);

  const handlePurchase = async () => {
    if (!user) {
      alert('Please sign in to make a purchase');
      return;
    }

    setPurchasing(true);
    try {
      // Use proper backend URL from environment variable
      const API_BASE = BACKEND_URL;
      const token = await getIdToken();

      let purchaseData = {
        listing_id: id,
        license_type: licenseType,
        purchase_type: purchaseType
      };

      // Add print configuration for print orders
      if (purchaseType === 'print') {
        purchaseData = {
          ...purchaseData,
          material: printConfig.material,
          quality: printConfig.quality,
          strength: printConfig.strength,
          smoothing: printConfig.smoothing,
          color: printConfig.color,
          quantity: printConfig.quantity
        };
      }

      // Use different endpoints for download vs print orders
      const endpoint = purchaseType === 'print' ? '/api/marketplace/print-order' : '/api/marketplace/purchases';
      
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(purchaseData)
      });

      const result = await response.json();

      if (response.ok) {
        if (purchaseType === 'download') {
          alert('Purchase successful! Your download will be available shortly.');
          // Here we would typically redirect to a download page or start the download
        } else {
          alert(`Print order successful! Production time: ${result.production_time}. Delivery: ${result.delivery_weeks} weeks.`);
          // Here we would typically redirect to order tracking page
        }
      } else {
        throw new Error(result.detail || 'Purchase failed');
      }
    } catch (error) {
      alert(error.message || 'Purchase failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading design...</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center p-6">
            <h2 className="text-xl font-semibold mb-4">Design Not Found</h2>
            <Button onClick={() => navigate('/marketplace')}>Back to Marketplace</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate current license price based on license type
  const getCurrentPrice = () => {
    return licenseType === 'Personal Use' ? listing.personal_price : listing.commercial_price;
  };
  
  const currentPrice = getCurrentPrice();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center relative">
            <Button 
              onClick={() => navigate('/marketplace')} 
              variant="outline"
              className="absolute left-0"
            >
              ← Back to Marketplace
            </Button>
            <button onClick={() => navigate('/')} className="flex items-center">
              <img 
                src="/logo-nav.png" 
                alt="Create 3D Works Logo" 
                className="h-10 sm:h-12 w-auto object-contain"
                style={{ minWidth: '120px' }}
              />
            </button>
            <button
              onClick={() => navigate('/cart')}
              className="absolute right-0 flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden sm:inline">Cart</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Design Preview */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                {/* 3D Preview or Image Gallery */}
                {listing.listing_type === 'assembly' ? (
                  // Assembly Preview
                  <div className="rounded-t-lg overflow-hidden">
                    <div className="bg-slate-50 p-4 border-b">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900">Assembly Preview</h3>
                        <Badge className="bg-purple-100 text-purple-800">Assembly ({listing.assembly_components?.length || 0} Parts)</Badge>
                      </div>
                    </div>
                    {listing.assembly_preview_type === 'image' && listing.assembly_preview_url ? (
                      <img 
                        src={`${BACKEND_URL}${listing.assembly_preview_url}`}
                        alt={`${listing.title} Assembly`}
                        className="w-full h-full object-cover"
                      />
                    ) : listing.assembly_preview_type === 'cad_file' && listing.assembly_preview_url ? (
                      <ThreeDPreview 
                        file={null}
                        cadFileUrl={`${BACKEND_URL}${listing.assembly_preview_url}`}
                        className="w-full"
                        onAnalysis={(data) => console.log('Assembly 3D analysis:', data)}
                      />
                    ) : (
                      <div className="aspect-square bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                        <Package className="w-32 h-32 text-purple-500" />
                      </div>
                    )}
                  </div>
                ) : (
                  // Individual Part Preview
                  listing.cad_file_url && (listing.has_3d_preview !== false) ? (
                    <div className="rounded-t-lg overflow-hidden">
                      <div className="bg-slate-50 p-4 border-b">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-slate-900">Interactive 3D Preview</h3>
                          <Badge className="bg-blue-100 text-blue-800">3D Model</Badge>
                        </div>
                      </div>
                      <ThreeDPreview 
                        file={null}
                        cadFileUrl={`${BACKEND_URL}/api/${listing.cad_file_url}`}
                        className="w-full"
                        onAnalysis={(data) => console.log('Marketplace 3D analysis:', data)}
                      />
                    </div>
                  ) : (
                    <div className="aspect-square bg-gradient-to-br from-blue-100 to-purple-100 rounded-t-lg flex items-center justify-center">
                      {listing.preview_images && listing.preview_images.length > 0 ? (
                        <img 
                          src={listing.preview_images[0]} 
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package className="w-32 h-32 text-blue-500" />
                      )}
                    </div>
                  )
                )}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 mb-2">{listing.title}</h1>
                      <p className="text-gray-600">
                        by{' '}
                        {listing.creator_username ? (
                          <button
                            onClick={() => navigate(`/creator/${listing.creator_username}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {listing.designer_display_name || listing.designer_name}
                          </button>
                        ) : (
                          <span className="font-medium">{listing.designer_display_name || listing.designer_name}</span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Upload className="w-4 h-4" />
                        <span>{listing.download_count} downloads</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                      <p className="text-gray-700 leading-relaxed">{listing.description}</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-sm text-gray-600">Category</Label>
                        <p className="font-medium">{listing.category}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Use Case</Label>
                        <p className="font-medium">{listing.use_case}</p>
                      </div>
                      {listing.dimensions && (
                        <div>
                          <Label className="text-sm text-gray-600">Dimensions</Label>
                          <p className="font-medium">{listing.dimensions}</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-sm text-gray-600">File Format</Label>
                        <p className="font-medium">
                          {listing.listing_type === 'assembly' && listing.assembly_components ? 
                            (() => {
                              const formats = [...new Set(listing.assembly_components.map(c => c.file_format))];
                              return formats.join(', ');
                            })()
                            : (listing.file_format || 'STL')
                          }
                        </p>
                      </div>
                    </div>

                    {listing.tags && listing.tags.length > 0 && (
                      <div>
                        <Label className="text-sm text-gray-600 mb-2 block">Tags</Label>
                        <div className="flex flex-wrap gap-2">
                          {listing.tags.map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Assembly Parts List - OUTSIDE space-y-4 container for proper spacing */}
                  {listing.listing_type === 'assembly' && listing.show_component_names && listing.assembly_components && (
                    <div className="mt-16 mb-6">
                      <h3 className="font-semibold text-gray-900 mb-2">Parts List ({listing.assembly_components.length} parts)</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <ul className="space-y-2">
                          {listing.assembly_components.map((comp, index) => (
                            <li key={comp.id} className="flex items-center text-sm">
                              <span className="font-mono text-gray-500 mr-2">{index + 1}.</span>
                              <span className="font-medium">{comp.display_name}</span>
                              <span className="text-gray-500 ml-2 text-xs">({comp.file_format})</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Purchase Panel */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Get This Design</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Purchase Type</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button
                      variant={purchaseType === 'download' ? 'default' : 'outline'}
                      onClick={() => setPurchaseType('download')}
                      className="text-sm"
                    >
                      Download
                    </Button>
                    <Button
                      variant={purchaseType === 'print' ? 'default' : 'outline'}
                      onClick={() => setPurchaseType('print')}
                      className="text-sm"
                    >
                      Print Order
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">License Type</Label>
                  <Select value={licenseType} onValueChange={setLicenseType}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Personal Use">Personal Use</SelectItem>
                      <SelectItem value="Commercial Use">Commercial Use</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Print Configuration Options */}
                {purchaseType === 'print' && listing.is_printable && (
                  <div className="space-y-4 border-t pt-4">
                    <h4 className="font-medium text-sm">Print Configuration</h4>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-medium">Material</Label>
                        <Select 
                          value={printConfig.material} 
                          onValueChange={(value) => setPrintConfig(prev => ({...prev, material: value}))}
                        >
                          <SelectTrigger className="text-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PLA">PLA - Standard</SelectItem>
                            <SelectItem value="PLA+">PLA+ - Reinforced</SelectItem>
                            <SelectItem value="Silk PLA">Silk PLA - Glossy finish</SelectItem>
                            <SelectItem value="Wood-fill PLA">Wood-fill PLA - Reinforced, Wood-like finish</SelectItem>
                            <SelectItem value="Metal-fill PLA">Metal-fill PLA - Reinforced, Metal-like finish</SelectItem>
                            <SelectItem value="CF PLA">CF PLA - Carbon Fiber reinforced</SelectItem>
                            <SelectItem value="HT PLA">HT PLA - High Temp PLA</SelectItem>
                            <SelectItem value="PETG">PETG</SelectItem>
                            <SelectItem value="CPE+">CPE+</SelectItem>
                            <SelectItem value="ABS">ABS</SelectItem>
                            <SelectItem value="ABS+">ABS+</SelectItem>
                            <SelectItem value="ASA">ASA</SelectItem>
                            <SelectItem value="TPU">TPU</SelectItem>
                            <SelectItem value="TPU 85A">TPU 85A</SelectItem>
                            <SelectItem value="TPU 64D">TPU 64D (semi-rigid)</SelectItem>
                            <SelectItem value="PC">PC - coming soon!</SelectItem>
                            <SelectItem value="PC-CF">PC-CF - coming soon!</SelectItem>
                            <SelectItem value="PA">PA - coming soon!</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Quality</Label>
                        <Select 
                          value={printConfig.quality} 
                          onValueChange={(value) => setPrintConfig(prev => ({...prev, quality: value}))}
                        >
                          <SelectTrigger className="text-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Concept">Concept</SelectItem>
                            <SelectItem value="Prototype">Prototype</SelectItem>
                            <SelectItem value="Test/Demo">Test/Demo</SelectItem>
                            <SelectItem value="End Use">End Use</SelectItem>
                            <SelectItem value="Display">Display</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Strength</Label>
                        <Select 
                          value={printConfig.strength} 
                          onValueChange={(value) => setPrintConfig(prev => ({...prev, strength: value}))}
                        >
                          <SelectTrigger className="text-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Cosmetic Only">Cosmetic Only</SelectItem>
                            <SelectItem value="Reinforced">Reinforced</SelectItem>
                            <SelectItem value="Functional">Functional</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Smoothing</Label>
                        <Select 
                          value={printConfig.smoothing} 
                          onValueChange={(value) => setPrintConfig(prev => ({...prev, smoothing: value}))}
                        >
                          <SelectTrigger className="text-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="None - As Printed">None - As Printed</SelectItem>
                            <SelectItem value="Light Sanding">Light Sanding</SelectItem>
                            <SelectItem value="Smooth Finish">Smooth Finish</SelectItem>
                            <SelectItem value="Premium Finish">Premium Finish</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Color</Label>
                        <Input
                          value={printConfig.color}
                          onChange={(e) => setPrintConfig(prev => ({...prev, color: e.target.value}))}
                          placeholder="Any"
                          className="text-sm mt-1"
                        />
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          max="10"
                          value={printConfig.quantity}
                          onChange={(e) => setPrintConfig(prev => ({...prev, quantity: parseInt(e.target.value) || 1}))}
                          className="text-sm mt-1"
                        />
                      </div>
                    </div>

                    {/* Shipping Destination */}
                    <div className="border-t pt-3 mt-3">
                      <h5 className="font-medium text-xs mb-3 text-gray-700">Shipping Destination</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-medium">Country</Label>
                          <Select 
                            value={printConfig.country_code} 
                            onValueChange={(value) => setPrintConfig(prev => ({...prev, country_code: value}))}
                          >
                            <SelectTrigger className="text-sm mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="US">United States</SelectItem>
                              <SelectItem value="CA">Canada</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-xs font-medium">Postal/Zip Code (Optional)</Label>
                          <Input
                            value={printConfig.postal_code}
                            onChange={(e) => setPrintConfig(prev => ({...prev, postal_code: e.target.value}))}
                            placeholder="Optional"
                            className="text-sm mt-1"
                          />
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1">
                        Shipping calculated from Dartmouth, NS, Canada. Postal/zip code helps provide more accurate shipping estimates.
                      </p>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-4">
                  {purchaseType === 'download' ? (
                    <>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Download License Price</span>
                        <span className="text-2xl font-bold text-green-600">
                          {formatPrice(currentPrice)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1 mb-2">
                        <p>• Instant download after purchase</p>
                        <p>• {licenseType} license included</p>
                        <p>• Unlimited downloads</p>
                      </div>
                    </>
                  ) : (
                    <>
                      {calculatingCost ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0007b5] mx-auto mb-2"></div>
                          <span className="text-sm text-gray-600">Calculating print cost...</span>
                        </div>
                      ) : printCost ? (
                        <div className="space-y-3">
                          <div className="flex justify-between items-center mb-3 pb-3 border-b-2 border-gray-300">
                            <span className="text-sm font-semibold text-gray-700">Total Order Cost</span>
                            <span className="text-2xl font-bold text-green-600">
                              {formatPrice(printCost.total_cost)}
                            </span>
                          </div>
                          
                          {/* License Cost Section */}
                          <div className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">License Fee ({licenseType})</span>
                              <span className="text-lg font-semibold text-gray-900">{formatPrice(printCost.license_fee)}</span>
                            </div>
                          </div>

                          {/* Print Order Section */}
                          <div className="space-y-1 pt-2 border-t">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Print Cost (Instant Quote)</span>
                              <span className="text-lg font-semibold text-gray-900">{formatPrice(printCost.print_cost)}</span>
                            </div>
                            
                            <div className="pl-3 space-y-0.5 text-[10px] text-gray-500">
                              <div className="flex justify-between">
                                <span>Quantity:</span>
                                <span>{printConfig.quantity}x @ {formatPrice(printCost.cost_per_item)} each</span>
                              </div>
                              <div className="flex justify-between font-medium text-gray-700 pt-1 border-t">
                                <span>Total Order Cost:</span>
                                <span>{formatPrice(printCost.total_cost)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Production Details */}
                          <div className="text-xs text-gray-600 space-y-1 pt-2 border-t bg-blue-50 rounded px-2 py-2 -mx-1">
                            {printCost.is_assembly && printCost.component_count && (
                              <div className="flex justify-between">
                                <span>📦 Components in assembly:</span>
                                <span className="font-medium">{printCost.component_count} parts</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span>⏱️ Production time:</span>
                              <span className="font-medium">{printCost.production_time}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>🚚 Delivery estimate:</span>
                              <span className="font-medium">{printCost.delivery_weeks} weeks</span>
                            </div>
                          </div>
                        </div>
                      ) : listing.is_printable ? (
                        <div className="text-center py-4">
                          <span className="text-sm text-gray-600">Configure print options to see pricing</span>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-red-600">
                          <span className="text-sm">This design is not available for printing</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <Button 
                  onClick={handlePurchase}
                  disabled={purchasing || !user || (purchaseType === 'print' && (!printCost || calculatingCost))}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-lg font-semibold py-3"
                >
                  {purchasing ? 'Processing...' : 
                   calculatingCost ? 'Calculating...' :
                   purchaseType === 'download' ? `Buy & Download - ${formatPrice(currentPrice)}` : 
                   `Order Print - ${printCost ? formatPrice(parseFloat(currentPrice) + parseFloat(printCost.total_cost)) : formatPrice(0)}`}
                </Button>

                {!user && (
                  <p className="text-xs text-gray-500 text-center">
                    Please sign in to make a purchase
                  </p>
                )}

                <div className="pt-4 border-t">
                  <div className="space-y-2 text-xs text-gray-500">
                    <p>✓ Secure payment processing</p>
                    <p>✓ Instant access after payment</p>
                    <p>✓ 30-day satisfaction guarantee</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

// Designer Edit Route
const DesignerEditRoute = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    use_case: '',
    personal_price: '',
    commercial_price: '',
    is_printable: true,
    tags: '',
    dimensions: '',
    dimensions_unit: 'mm',
    dimensions_unit_custom: '',
    complexity_level: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const categories = [
    'Automotive', 'Home & Living', 'Garden & Outdoor', 'Organization', 
    'Electronics', 'Toys & Games', 'Art & Decorative', 'Tools & Hardware',
    'Fashion & Jewelry', 'Replacement Parts', 'Sim Racing', 'Flight Simulator', 'Other'
  ];

  const useCases = ['Functional', 'Decorative', 'Replacement', 'Educational', 'Prototype'];

  // Load listing data
  useEffect(() => {
    const fetchListing = async () => {
      if (!user || !id) return;

      try {
        const API_BASE = BACKEND_URL;
        const token = await getIdToken();
        
        const response = await fetch(`${API_BASE}/api/marketplace/listings/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const listingData = await response.json();
          
          // Verify this listing belongs to the current user
          if (listingData.designer_id !== user.id) {
            alert('You can only edit your own listings');
            navigate('/designer/dashboard');
            return;
          }

          setListing(listingData);
          setFormData({
            title: listingData.title || '',
            description: listingData.description || '',
            category: listingData.category || '',
            use_case: listingData.use_case || '',
            personal_price: listingData.personal_price?.toString() || '',
            commercial_price: listingData.commercial_price?.toString() || '',
            is_printable: listingData.is_printable || true,
            tags: (listingData.tags || []).join(', '),
            dimensions: listingData.dimensions || '',
            dimensions_unit: listingData.dimensions_unit || 'mm',
            dimensions_unit_custom: listingData.dimensions_unit_custom || '',
            complexity_level: listingData.complexity_level || ''
          });
        } else {
          throw new Error('Failed to fetch listing');
        }
      } catch (error) {
        console.error('Error fetching listing:', error);
        alert('Failed to load listing for editing');
        navigate('/designer/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [user, id, navigate, getIdToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_BASE = BACKEND_URL;
      const token = await getIdToken();

      const updateData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        use_case: formData.use_case,
        personal_price: parseFloat(formData.personal_price),
        commercial_price: parseFloat(formData.commercial_price),
        is_printable: formData.is_printable,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        dimensions: formData.dimensions,
        dimensions_unit: formData.dimensions_unit === 'other' ? formData.dimensions_unit_custom : formData.dimensions_unit,
        complexity_level: formData.complexity_level
      };

      const response = await fetch(`${API_BASE}/api/marketplace/listings/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Listing updated successfully!'
        });
        setTimeout(() => navigate('/designer/dashboard'), 2000);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating listing:', error);
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to update listing'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <SharedPageLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Required</h2>
          <p className="text-gray-600">Please sign in to edit listings.</p>
        </div>
      </SharedPageLayout>
    );
  }

  if (loading) {
    return (
      <SharedPageLayout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
          <p>Loading listing...</p>
        </div>
      </SharedPageLayout>
    );
  }

  if (!listing) {
    return (
      <SharedPageLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Listing Not Found</h2>
          <Button onClick={() => navigate('/designer/dashboard')}>Back to Dashboard</Button>
        </div>
      </SharedPageLayout>
    );
  }

  return (
    <SharedPageLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Edit className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Edit Design</h1>
            </div>
            <Button onClick={() => navigate('/designer/dashboard')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Design Information</CardTitle>
              <CardDescription>Update your design details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Design Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData(prev => ({...prev, category: value}))}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                  required
                  className="mt-2"
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="use_case">Use Case *</Label>
                  <Select 
                    value={formData.use_case} 
                    onValueChange={(value) => setFormData(prev => ({...prev, use_case: value}))}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select use case" />
                    </SelectTrigger>
                    <SelectContent>
                      {useCases.map(useCase => (
                        <SelectItem key={useCase} value={useCase}>{useCase}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="complexity">Complexity Level</Label>
                  <Input
                    id="complexity"
                    value={formData.complexity_level}
                    onChange={(e) => setFormData(prev => ({...prev, complexity_level: e.target.value}))}
                    placeholder="e.g., Beginner, Intermediate, Advanced"
                    className="mt-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle>Pricing</CardTitle>
              <CardDescription>Set your download prices</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="personal_price">Personal Use Price ($) *</Label>
                  <Input
                    id="personal_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.personal_price}
                    onChange={(e) => setFormData(prev => ({...prev, personal_price: e.target.value}))}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="commercial_price">Commercial Use Price ($) *</Label>
                  <Input
                    id="commercial_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.commercial_price}
                    onChange={(e) => setFormData(prev => ({...prev, commercial_price: e.target.value}))}
                    required
                    className="mt-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Details */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tags">Tags (comma separated)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({...prev, tags: e.target.value}))}
                    placeholder="e.g., mechanical, automotive, functional"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="dimensions">Dimensions</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="dimensions"
                      type="text"
                      placeholder="150 x 100 x 50"
                      value={formData.dimensions}
                      onChange={(e) => setFormData(prev => ({...prev, dimensions: e.target.value}))}
                      className="flex-1"
                    />
                    <Select 
                      value={formData.dimensions_unit} 
                      onValueChange={(value) => setFormData(prev => ({...prev, dimensions_unit: value}))}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mm">mm</SelectItem>
                        <SelectItem value="cm">cm</SelectItem>
                        <SelectItem value="in">in</SelectItem>
                        <SelectItem value="ft">ft</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.dimensions_unit === 'other' && (
                    <Input
                      type="text"
                      placeholder="Enter custom unit"
                      value={formData.dimensions_unit_custom}
                      onChange={(e) => setFormData(prev => ({...prev, dimensions_unit_custom: e.target.value}))}
                      className="mt-2"
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Status */}
          {submitStatus && (
            <div className={`rounded-lg p-4 ${
              submitStatus.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {submitStatus.message}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/designer/dashboard')}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-[#0007b5] hover:bg-[#000690]"
            >
              {isSubmitting ? 'Updating...' : 'Update Listing'}
            </Button>
          </div>
        </form>
      </div>
    </SharedPageLayout>
  );
};

// Designer Terms Route
const DesignerTermsRoute = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center space-x-3"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-[#0007b5] to-[#000690] bg-clip-text text-transparent">
                Create 3D Works
              </h1>
            </button>
            <button
              onClick={() => navigate('/designer/apply')}
              className="text-slate-600 hover:text-slate-800 transition-colors"
            >
              ← Back to Application
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Designer Terms & Conditions</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-red-900 mb-3">⚠️ Important Legal Notice</h2>
            <p className="text-red-800">
              By uploading designs to our marketplace, you acknowledge and agree to the terms below. 
              Please read carefully before proceeding.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Intellectual Property Ownership</h2>
            <p className="text-slate-700 mb-4">
              You represent and warrant that you are the sole owner of all intellectual property rights 
              in the designs you upload, or you have obtained all necessary permissions, licenses, and 
              authorizations to use, distribute, and commercialize such designs.
            </p>
            <p className="text-slate-700 mb-4">
              You retain ownership of your original designs. By uploading to our platform, you grant 
              Create 3D Works a non-exclusive license to display, distribute, and facilitate the sale 
              of your designs through our marketplace.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Content Liability & Indemnification</h2>
            <p className="text-slate-700 mb-4">
              <strong>You agree to indemnify and hold harmless Create 3D Works, its officers, directors, 
              employees, and agents from and against any and all claims, damages, obligations, losses, 
              liabilities, costs, and expenses arising from:</strong>
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Your uploaded designs or any content you provide</li>
              <li>Your breach of these terms or violation of any law or regulation</li>
              <li>Any claim that your designs infringe on third-party intellectual property rights</li>
              <li>Any damages or losses resulting from the use of your designs by customers</li>
              <li>Any defects, errors, or safety issues in your uploaded designs</li>
            </ul>
            <p className="text-slate-700 mb-4">
              Create 3D Works assumes no responsibility for the content, accuracy, or legal status 
              of designer-uploaded materials.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Content Standards & Restrictions</h2>
            <p className="text-slate-700 mb-4">Uploaded designs must not:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Infringe on any third-party intellectual property rights</li>
              <li>Contain illegal, harmful, or offensive content</li>
              <li>Be intended for weapons, illegal activities, or harmful purposes</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Contain malicious code or corrupted data</li>
              <li>Be false, misleading, or deceptive in description or function</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibent text-slate-900 mb-4">4. Revenue Sharing & Payments</h2>
            <p className="text-slate-700 mb-4">
              Revenue sharing percentages and payment terms will be communicated separately upon 
              designer approval. Create 3D Works reserves the right to modify revenue sharing terms 
              with 30 days written notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Limitation of Liability</h2>
            <p className="text-slate-700 mb-4">
              Create 3D Works shall not be liable for any indirect, incidental, special, consequential, 
              or punitive damages arising from or related to your use of the marketplace platform or 
              any customer's use of your designs.
            </p>
            <p className="text-slate-700 mb-4">
              Our total liability shall not exceed the total amount paid to you in the preceding 12 months.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Platform Rights</h2>
            <p className="text-slate-700 mb-4">
              Create 3D Works reserves the right to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Review and approve or reject any submitted designs</li>
              <li>Remove designs that violate these terms</li>
              <li>Suspend or terminate designer accounts for violations</li>
              <li>Modify platform features and functionality</li>
              <li>Update these terms with reasonable notice</li>
            </ul>
          </section>

          <div className="bg-blue-50 rounded-lg p-6 mt-8">
            <p className="text-blue-800 text-sm">
              <strong>By proceeding with your designer application, you acknowledge that you have read, 
              understood, and agree to be bound by these terms and conditions.</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Customer License Terms Route
const CustomerLicenseTermsRoute = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center space-x-3"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-[#0007b5] to-[#000690] bg-clip-text text-transparent">
                Create 3D Works
              </h1>
            </button>
            <button
              onClick={() => navigate('/marketplace')}
              className="text-slate-600 hover:text-slate-800 transition-colors"
            >
              ← Back to Designs
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Design License Terms</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-blue-900 mb-3">📄 License Summary</h2>
            <p className="text-blue-800">
              When you purchase a design from our marketplace, you receive a license to use the design 
              according to the terms below. This is not a transfer of ownership.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. License Grant</h2>
            <p className="text-slate-700 mb-4">
              Upon successful purchase, you are granted a <strong>non-exclusive, non-transferable license</strong> 
              to use the purchased 3D design file according to the license type you selected:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="font-semibold text-green-900 mb-2">Personal Use License</h3>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>• Personal projects and hobbies</li>
                  <li>• Non-commercial use only</li>
                  <li>• Cannot resell printed items</li>
                  <li>• Cannot redistribute design files</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-2">Commercial Use License</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Commercial and business use</li>
                  <li>• Can sell printed items</li>
                  <li>• Cannot resell or redistribute design files</li>
                  <li>• Cannot sublicense to others</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Restrictions</h2>
            <p className="text-slate-700 mb-4">You may NOT:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Resell, redistribute, or share the design files with others</li>
              <li>Reverse engineer or create derivative works without permission</li>
              <li>Use the designs for illegal or harmful purposes</li>
              <li>Claim ownership or authorship of the designs</li>
              <li>Transfer your license to another person or entity</li>
              <li>Upload the designs to other marketplaces or platforms</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Design Ownership</h2>
            <p className="text-slate-700 mb-4">
              All designs remain the intellectual property of their original creators (designers). 
              Your purchase grants you usage rights only, not ownership rights. The designer retains 
              all copyrights, patents, and other intellectual property rights in the designs.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Download & Access</h2>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Downloads are available immediately after successful payment</li>
              <li>Unlimited downloads of purchased designs for licensed use</li>
              <li>Files are provided "as-is" without warranty</li>
              <li>You are responsible for ensuring compatibility with your equipment</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibent text-slate-900 mb-4">5. Liability Disclaimer</h2>
            <p className="text-slate-700 mb-4">
              Create 3D Works provides designs "as-is" without any warranty. We are not responsible for:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Print quality or dimensional accuracy</li>
              <li>Material compatibility or performance</li>
              <li>Fitness for any particular purpose</li>
              <li>Safety or structural integrity of printed items</li>
              <li>Any damages resulting from use of the designs</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Refund Policy</h2>
            <p className="text-slate-700 mb-4">
              Due to the digital nature of design files, all sales are final. Refunds may be considered 
              only in cases of technical issues preventing download or fundamental errors in the design file.
            </p>
          </section>

          <div className="bg-slate-100 rounded-lg p-6 mt-8">
            <p className="text-slate-700 text-sm">
              <strong>By purchasing and downloading designs from our marketplace, you acknowledge that you have 
              read, understood, and agree to be bound by these license terms.</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;